import { useState, useCallback, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Popover, PopoverTrigger, PopoverContent, PopoverAnchor } from "@/components/ui/popover";
import api, { getApiUrl } from "@/lib/api";
import {
  Plus, Trash2, Save, ChevronDown,
  Hash, CheckSquare, Type, X, Minus,
  AlignLeft, UserCircle2, Trophy, Paperclip,
  GripVertical, GripHorizontal, Image as ImageIcon, StickyNote, Pencil,
  ZoomIn, ZoomOut, Check, Lock, Unlock, BookTemplate, Loader2, MousePointerClick, Rows3, Square, Sigma,
} from "lucide-react";
import { toast } from "sonner";

const API = `${getApiUrl()}/api`;

const SYMBOL_OPTIONS = ["\u2713", "\u2717", "!", "?", "\u2605", "\u25CF", "\u25CB", "\u25B3", "\u25A2", "\u2014"];

const ROW_COLORS = [
  // Neutrals
  "#ffffff", "#e4e4e7", "#a1a1aa", "#71717a", "#3f3f46", "#18181b",
  // Warm
  "#fef3c7", "#d4a574", "#92400e", "#78350f",
  // Red
  "#fca5a5", "#ef4444", "#b91c1c", "#7f1d1d",
  // Orange
  "#fdba74", "#f97316", "#c2410c",
  // Yellow
  "#fde68a", "#eab308", "#a16207",
  // Green
  "#86efac", "#22c55e", "#15803d", "#14532d",
  // Blue
  "#93c5fd", "#3b82f6", "#1d4ed8", "#1e3a5f",
  // Purple
  "#c4b5fd", "#8b5cf6", "#6d28d9", "#4c1d95",
  // Pink
  "#f9a8d4", "#ec4899", "#be185d",
  // Cyan / Teal
  "#67e8f9", "#06b6d4", "#0e7490",
];

const CELL_TYPES = {
  checkbox: { icon: CheckSquare, label: "Checkbox", color: "text-purple-400" },
  number: { icon: Hash, label: "Number", color: "text-emerald-400" },
  symbol: { icon: Type, label: "Symbol", color: "text-amber-400" },
  text: { icon: AlignLeft, label: "Text", color: "text-sky-400" },
  character: { icon: UserCircle2, label: "Character", color: "text-pink-400" },
  achievement: { icon: Trophy, label: "Achievement", color: "text-yellow-400" },
  image: { icon: ImageIcon, label: "Image URL", color: "text-teal-400" },
  note: { icon: StickyNote, label: "Note", color: "text-amber-300" },
};

const defaultCellValue = (type) => {
  if (type === "checkbox") return false;
  if (type === "number") return 0;
  if (type === "character" || type === "achievement" || type === "note") return null;
  if (type === "image") return "";
  return "";
};

// Truthy when the cell holds user-entered data (as opposed to the pristine
// default a fresh cell of its type would carry). Used before bulk type
// conversions to warn the user that they're about to overwrite work.
const cellHasData = (cell) => {
  if (!cell) return false;
  const v = cell.value;
  if (cell.type === "checkbox") return v === true;
  if (cell.type === "number") return typeof v === "number" && v !== 0;
  if (cell.type === "text" || cell.type === "symbol") return typeof v === "string" && v.trim() !== "";
  if (cell.type === "image") return typeof v === "string" && v.trim() !== "";
  if (cell.type === "character" || cell.type === "achievement" || cell.type === "note") {
    if (!v) return false;
    if (typeof v === "string") return v.trim() !== "";
    // Cell entity values are stored as {characterId|achievementId|noteId, ...}
    // (not {id}), so check those keys too — otherwise a filled entity cell is
    // wrongly treated as empty and bulk conversions wipe the user's work.
    return !!(v.id || v.entityId || v.characterId || v.achievementId || v.noteId);
  }
  return false;
};

// When the grid grows past this many data columns, rotate column headers
// 45 degrees and shrink their width to keep the table on one screen.
const ROTATE_HEADER_THRESHOLD = 6;

// Compact popover styling used by all in-grid menus so they escape the
// grid's overflow-x-auto clipping (Radix Popover portals to document.body).
const POPOVER_MENU_CN = "bg-zinc-800 border border-zinc-700 text-zinc-200 p-1 w-auto max-w-none shadow-xl rounded-lg";

export default function ScorecardEditor({ scorecard, onSave, onCancel, compact = false, characters: charactersProp, achievements: achievementsProp, notes: notesProp, masterGameId: masterGameIdProp }) {
  const [name, setName] = useState(scorecard?.name || "Grid");
  const [rows, setRows] = useState(scorecard?.rows || [
    { id: crypto.randomUUID(), name: "", color: "#a1a1aa" },
    { id: crypto.randomUUID(), name: "", color: "#a1a1aa" },
    { id: crypto.randomUUID(), name: "", color: "#a1a1aa" },
  ]);
  const [cols, setCols] = useState(scorecard?.cols || [
    { id: crypto.randomUUID(), name: "" },
    { id: crypto.randomUUID(), name: "" },
    { id: crypto.randomUUID(), name: "" },
  ]);
  const [cells, setCells] = useState(scorecard?.cells || {});
  // Column / row groups — each group has {id, name, color}, and each
  // row/col carries an optional `groupId` that references it. Consecutive
  // rows/cols with the same groupId render under a shared secondary
  // header bar and get a subtle background tint. See spec: "grid sections".
  const [colGroups, setColGroups] = useState(scorecard?.colGroups || []);
  const [rowGroups, setRowGroups] = useState(scorecard?.rowGroups || []);
  // Default cell type used for freshly-added rows/columns AND as the
  // target of the "Apply to all cells" bulk action. Persisted with the
  // grid so re-opening remembers the choice. Falls back to "checkbox".
  const [defaultCellType, setDefaultCellType] = useState(scorecard?.defaultCellType || "checkbox");
  // Per-cell "check / X / unmarked" overlay layer used by template-based
  // grids so users can tick off cells over the top of locked content.
  // Key = `${rowId}:${colId}`, value = "check" | "x" | "" (empty removes).
  const [cellMarks, setCellMarks] = useState(scorecard?.cellMarks || {});
  // `fromTemplateId` on the scorecard marks it as a template instance.
  // When set, we default to Marks mode and disable structural edits so
  // clicking a cell toggles a mark instead of opening the type picker.
  // Users can hit the Unlock button to re-enable edits.
  const [fromTemplateId, setFromTemplateId] = useState(scorecard?.fromTemplateId || null);
  const [locked, setLocked] = useState(!!scorecard?.fromTemplateId);
  const [marksMode, setMarksMode] = useState(!!scorecard?.fromTemplateId);
  // Grid image zoom — 1 = full 64px, 0.5 = compact 32px, 0.25 = tiny 16px.
  // Persisted with the grid so different grids on the same page can have
  // different zooms.
  const [zoom, setZoom] = useState([1, 0.5, 0.25].includes(scorecard?.zoom) ? scorecard.zoom : 1);
  // Marks-mode check counters: when on, a trailing count cell is shown at the
  // far end of each row and the bottom of each column (opposite the title),
  // tallying the green check marks. Only meaningful in Marks On mode.
  const [showCounts, setShowCounts] = useState(!!scorecard?.showCounts);
  const imgMaxPx = zoom === 0.25 ? 16 : zoom === 0.5 ? 32 : 64;
  const [editingHeader, setEditingHeader] = useState(null);
  const [colorPicker, setColorPicker] = useState(null);
  const [cellTypeMenu, setCellTypeMenu] = useState(null);
  const [colTypeMenu, setColTypeMenu] = useState(null);
  const [rowTypeMenu, setRowTypeMenu] = useState(null);
  // 3-way confirm shown before bulk cell-type conversions that would
  // clobber user-entered data. `overwrite` = clear + convert all,
  // `keep` = convert only empty cells, `cancel` = abort. Rendered as
  // a modal at the bottom of the editor. Payload: { message, apply }
  // where `apply(mode: "overwrite" | "keep")` does the mutation.
  const [typeChangeConfirm, setTypeChangeConfirm] = useState(null);
  const [symbolPicker, setSymbolPicker] = useState(null);
  const [characterPicker, setCharacterPicker] = useState(null);
  const [achievementPicker, setAchievementPicker] = useState(null);
  // Cell picker for the new "note" cell type — value is `${rowId}:${colId}`.
  const [notePicker, setNotePicker] = useState(null);
  // Cell picker for the new "image" cell type — value is `${rowId}:${colId}`.
  const [imagePicker, setImagePicker] = useState(null);
  // Row/col anchor picker — key is `row:<id>` or `col:<id>`.
  // Value is { kind: "character"|"achievement" } after user chooses type,
  // or null to show the kind chooser first.
  const [anchorPicker, setAnchorPicker] = useState(null);
  const [dirty, setDirty] = useState(false);
  // Optional top-left corner content — declared early so rowLabelColWidthRem
  // can widen the column to fit a tweet-length description.
  const [corner, setCorner] = useState(scorecard?.corner || null);
  const [cornerImgInput, setCornerImgInput] = useState("");
  const [cornerOpen, setCornerOpen] = useState(false);
  const [cornerDraft, setCornerDraft] = useState(null);

  // Fetch character notes and achievements lazily when caller didn't
  // already pass them in. Both come from the game document — characters
  // live in `game.notes` (noteType="character"), achievements are their
  // own collection queried per game. Notes with an imageUrl (any plain
  // note the user attached an image to) are also collected so the grid
  // can offer them as row/col/cell anchors.
  const [fetchedCharacters, setFetchedCharacters] = useState(null);
  const [fetchedAchievements, setFetchedAchievements] = useState(null);
  const [fetchedNotes, setFetchedNotes] = useState(null);
  useEffect(() => {
    const gid = scorecard?.gameId;
    if (!gid) return;
    let cancelled = false;
    const jobs = [];
    if (charactersProp === undefined || notesProp === undefined) {
      jobs.push((async () => {
        try {
          const res = await api.get(`${API}/games/${gid}`);
          if (cancelled) return;
          const notes = Array.isArray(res.data?.notes) ? res.data.notes : [];
          if (charactersProp === undefined) {
            setFetchedCharacters(notes.filter((n) => n.noteType === "character"));
          }
          if (notesProp === undefined) {
            // Only plain "note" type notes are eligible as grid anchors —
            // character/map/checklist notes have their own dedicated slots.
            setFetchedNotes(notes.filter((n) => (n.noteType || "note") === "note"));
          }
        } catch (e) {
          if (!cancelled) {
            if (charactersProp === undefined) setFetchedCharacters([]);
            if (notesProp === undefined) setFetchedNotes([]);
          }
        }
      })());
    }
    if (achievementsProp === undefined) {
      jobs.push((async () => {
        try {
          const res = await api.get(`${API}/games/${gid}/achievements-grouped`);
          if (cancelled) return;
          const groups = res.data || {};
          // Stamp each achievement with a human-readable `_source` label so
          // the entity picker can show where it lives (base game, section,
          // DLC name, or Challenges). Prevents users having to guess between
          // duplicate names across DLCs when picking a target.
          const baseLabel = "Base Game";
          const flat = [
            ...((groups.baseGame?.achievements) || []).map(a => ({ ...a, _source: baseLabel })),
            ...((groups.baseGame?.sections) || []).flatMap(s =>
              (s.achievements || []).map(a => ({ ...a, _source: s.name || baseLabel }))
            ),
            ...((groups.dlcs) || []).flatMap(d =>
              (d.achievements || []).map(a => ({ ...a, _source: d.name || "DLC" }))
            ),
            ...((groups.challenges?.achievements) || []).map(a => ({ ...a, _source: "Challenges" })),
          ];
          setFetchedAchievements(flat);
        } catch (e) {
          if (!cancelled) setFetchedAchievements([]);
        }
      })());
    }
    Promise.all(jobs);
    return () => { cancelled = true; };
  }, [charactersProp, achievementsProp, notesProp, scorecard?.gameId]);
  const characters = charactersProp ?? fetchedCharacters ?? [];
  const achievements = achievementsProp ?? fetchedAchievements ?? [];
  const notes = notesProp ?? fetchedNotes ?? [];

  // Row/col labels drove a lot of empty vertical/horizontal space when
  // labels are short (fixed h-[9.5rem] for the rotated header row and
  // w-48 for the row-label column). We now size those two dimensions
  // from the longest label so the header/left-gutter shrinks when
  // there's no long text to accommodate. Formulas:
  //   • Rotated header height ≈ full width of the longest label when the
  //     column titles are set vertical (90°), clamped to a sane range.
  //   • Row-label column width ≈ longest label width + button padding.
  const rotatedHeaderHeightRem = useMemo(() => {
    let longest = 0;
    cols.forEach((c) => {
      if (c.isDivider) return;
      const label = (c.name || "").trim();
      // Only expand for real labels; the placeholder "Col N" is fine at
      // the compact min height so short unnamed grids stay tight.
      if (label) longest = Math.max(longest, label.length);
    });
    // At text-xs font-medium ~0.42rem/char. Vertical (90°) text needs the
    // FULL label length as height (no diagonal shrink). Add ~1rem for the
    // icon+X row above and clamp so very long labels don't run off-screen.
    const rem = Math.max(3, Math.min(12, longest * 0.42 + 1));
    return rem;
  }, [cols]);
  const rowLabelColWidthRem = useMemo(() => {
    let longest = 0;
    rows.forEach((r) => {
      if (r.isDivider) return;
      const label = (r.name || "").trim();
      if (label) longest = Math.max(longest, label.length);
    });
    // Row labels render text-xs font-medium ~0.42rem/char. Add ~2rem
    // for the color-swatch button + gap. Clamp between 6rem and 12rem so
    // an empty label doesn't collapse the column below usability.
    const base = Math.max(6, Math.min(12, longest * 0.42 + 2));
    // When the corner holds a description, widen the column so a tweet-
    // length note wraps into a few readable lines instead of being cut off.
    if (corner?.text) return Math.max(base, 15);
    return base;
  }, [rows, corner]);

  // Fast lookups for anchored row/col headers so we can render live entity
  // data (e.g. an updated character portrait) instead of stale copy.
  const charById = useMemo(() => {
    const m = {};
    characters.forEach(c => { m[c.id] = c; });
    return m;
  }, [characters]);
  const achById = useMemo(() => {
    const m = {};
    achievements.forEach(a => { m[a.id] = a; });
    return m;
  }, [achievements]);
  const noteById = useMemo(() => {
    const m = {};
    notes.forEach(n => { m[n.id] = n; });
    return m;
  }, [notes]);

  // Resolve masterGameId — prefer prop, fall back to fetching the game doc.
  // Templates are scoped per-master-game so users share the same pool of
  // templates for a given canonical game across their own game copies.
  const [resolvedMasterGameId, setResolvedMasterGameId] = useState(masterGameIdProp || null);
  useEffect(() => {
    if (masterGameIdProp) { setResolvedMasterGameId(masterGameIdProp); return; }
    const gid = scorecard?.gameId;
    if (!gid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`${API}/games/${gid}`);
        if (!cancelled) setResolvedMasterGameId(res.data?.masterGameId || null);
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [masterGameIdProp, scorecard?.gameId]);

  // Template state — user-owned + approved community templates for the
  // scorecard's master game. Lazily loaded when the picker opens.
  const [templates, setTemplates] = useState([]);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [newTemplatePublic, setNewTemplatePublic] = useState(false);
  const loadTemplates = useCallback(async () => {
    if (!resolvedMasterGameId) { setTemplates([]); return; }
    setTemplatesLoading(true);
    try {
      const res = await api.get(`${API}/scorecards/templates`, {
        params: { master_game_id: resolvedMasterGameId },
      });
      setTemplates(res.data || []);
    } catch { /* silent */ }
    finally { setTemplatesLoading(false); }
  }, [resolvedMasterGameId]);

  // When the user picks a template, replace the grid's structure with
  // the template's rows/cols/cellDefaults, flag the grid as template-
  // based (`fromTemplateId`), and default to Marks mode + Locked so
  // clicks tick cells rather than editing them.
  const applyTemplate = (t) => {
    if (!t) return;
    // Clone rows/cols with fresh ids so template edits later don't
    // leak between grids (the ids only need to be unique per-grid).
    const newRows = (t.rows || []).map((r) => ({ ...r, id: r.id || crypto.randomUUID() }));
    const newCols = (t.cols || []).map((c) => ({ ...c, id: c.id || crypto.randomUUID() }));
    // Remap cell keys from old row/col ids to new ones.
    const idMapR = {};
    (t.rows || []).forEach((r, i) => { idMapR[r.id] = newRows[i].id; });
    const idMapC = {};
    (t.cols || []).forEach((c, i) => { idMapC[c.id] = newCols[i].id; });
    const newCells = {};
    Object.entries(t.cellDefaults || {}).forEach(([k, v]) => {
      const [rId, cId] = k.split(":");
      const nk = `${idMapR[rId] || rId}:${idMapC[cId] || cId}`;
      newCells[nk] = v;
    });
    setRows(newRows);
    setCols(newCols);
    setCells(newCells);
    setCellMarks({});
    setFromTemplateId(t.id);
    setLocked(true);
    setMarksMode(true);
    setTemplatesOpen(false);
    setDirty(true);
    toast.success(`Applied template "${t.name}"`);
  };

  // Save the current grid as a template so it can be re-used on other
  // copies of the same game. Ownership is per-user; publishing goes
  // through the existing admin moderation queue.
  const saveAsTemplate = async () => {
    if (!scorecard?.id) {
      toast.error("Save the grid first, then save it as a template.");
      return;
    }
    const finalName = (newTemplateName || name || "Grid").trim();
    setSavingTemplate(true);
    try {
      await api.post(`${API}/scorecards/from-scorecard/${scorecard.id}/template`, {
        name: finalName,
        description: "",
        requestPublic: !!newTemplatePublic,
      });
      toast.success(
        newTemplatePublic
          ? `Template "${finalName}" saved & submitted for review`
          : `Template "${finalName}" saved`
      );
      setSaveTemplateOpen(false);
      setNewTemplateName("");
      setNewTemplatePublic(false);
      await loadTemplates();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  // Sync the two "mode" states — locking always turns marks-mode on;
  // unlocking releases marks-mode so structural edits open pickers again.
  const toggleLocked = () => {
    setLocked((prev) => {
      const nl = !prev;
      setMarksMode(nl);
      return nl;
    });
  };

  // Resolve the live entity for an anchored row/col header. Falls back to
  // whatever snapshot was stored on the row/col if the live entity has been
  // deleted since the grid was saved.
  const resolveAnchorEntity = (entityType, entityId) => {
    if (entityType === "character") return charById[entityId] || null;
    if (entityType === "achievement") return achById[entityId] || null;
    if (entityType === "note") return noteById[entityId] || null;
    // "image" anchors carry the URL directly on the row/col — no lookup.
    return null;
  };

  const cellKey = (rowId, colId) => `${rowId}:${colId}`;

  const getCell = (rowId, colId) => {
    const key = cellKey(rowId, colId);
    return cells[key] || { type: "checkbox", value: false };
  };

  const setCell = useCallback((rowId, colId, updates) => {
    const key = cellKey(rowId, colId);
    setCells(prev => ({
      ...prev,
      [key]: { ...prev[key] || { type: "checkbox", value: false }, ...updates },
    }));
    setDirty(true);
  }, []);

  const setCellType = useCallback((rowId, colId, type) => {
    setCell(rowId, colId, { type, value: defaultCellValue(type) });
    setCellTypeMenu(null);
  }, [setCell]);

  const setColumnType = useCallback((colId, type) => {
    const filledKeys = [];
    rows.forEach(row => {
      if (row.isDivider) return;
      const cell = getCell(row.id, colId);
      if (cell.type !== type && cellHasData(cell)) filledKeys.push(cellKey(row.id, colId));
    });
    const apply = (mode) => {
      setCells(prev => {
        const next = { ...prev };
        rows.forEach(row => {
          if (row.isDivider) return;
          const key = cellKey(row.id, colId);
          if (mode === "keep" && filledKeys.includes(key)) return;
          next[key] = { type, value: defaultCellValue(type) };
        });
        return next;
      });
      setDirty(true);
    };
    setColTypeMenu(null);
    if (filledKeys.length === 0) { apply("overwrite"); return; }
    setTypeChangeConfirm({
      title: `Change column type to ${type}?`,
      message: `Some cells in this column already have data (${filledKeys.length}).`,
      targetType: type,
      filledCount: filledKeys.length,
      apply,
    });
  }, [rows, cells]);

  // ============ GROUPS ============
  // Groups are stored at scorecard level (colGroups/rowGroups) so their
  // metadata can be edited independently of member cols/rows. Membership
  // is via `groupId` on the col/row itself so drag-reordering preserves
  // the association automatically. When you drop a col into the middle
  // of another group's contiguous range, we set its `groupId` to match
  // its new neighbors (both sides in the same group) — matching the
  // spec: "adds the column/row to the group".

  // Reorder helper — when a col/row is assigned to (or removed from) a
  // group, we compact all members of each group so they sit adjacent in
  // the grid. Otherwise the secondary group header bar can only span
  // whichever members happen to already be next to each other, leaving
  // scattered "orphans" outside the visible band. Ungrouped items keep
  // their relative positions between groups.
  const reflowByGroups = (items) => {
    const seen = new Set();
    const result = [];
    for (const it of items) {
      if (seen.has(it.id)) continue;
      if (!it.groupId) {
        result.push(it);
        seen.add(it.id);
        continue;
      }
      // First time we see this group — pull all its members together.
      const members = items.filter(x => x.groupId === it.groupId && !seen.has(x.id));
      for (const m of members) {
        result.push(m);
        seen.add(m.id);
      }
    }
    return result;
  };

  const addColGroup = (name, color, colIds) => {
    const gid = crypto.randomUUID();
    setColGroups(prev => [...prev, { id: gid, name: name || "Group", color: color || "#3b82f6" }]);
    if (colIds && colIds.length > 0) {
      setCols(prev => reflowByGroups(prev.map(c => (colIds.includes(c.id) ? { ...c, groupId: gid } : c))));
    }
    setDirty(true);
    return gid;
  };
  const updateColGroup = (gid, patch) => {
    setColGroups(prev => prev.map(g => (g.id === gid ? { ...g, ...patch } : g)));
    setDirty(true);
  };
  const deleteColGroup = (gid) => {
    setColGroups(prev => prev.filter(g => g.id !== gid));
    setCols(prev => reflowByGroups(prev.map(c => (c.groupId === gid ? { ...c, groupId: null } : c))));
    setDirty(true);
  };
  const assignColToGroup = (colId, gid) => {
    setCols(prev => reflowByGroups(prev.map(c => (c.id === colId ? { ...c, groupId: gid || null } : c))));
    setDirty(true);
  };

  const addRowGroup = (name, color, rowIds) => {
    const gid = crypto.randomUUID();
    setRowGroups(prev => [...prev, { id: gid, name: name || "Group", color: color || "#3b82f6" }]);
    if (rowIds && rowIds.length > 0) {
      setRows(prev => reflowByGroups(prev.map(r => (rowIds.includes(r.id) ? { ...r, groupId: gid } : r))));
    }
    setDirty(true);
    return gid;
  };
  const updateRowGroup = (gid, patch) => {
    setRowGroups(prev => prev.map(g => (g.id === gid ? { ...g, ...patch } : g)));
    setDirty(true);
  };
  const deleteRowGroup = (gid) => {
    setRowGroups(prev => prev.filter(g => g.id !== gid));
    setRows(prev => reflowByGroups(prev.map(r => (r.groupId === gid ? { ...r, groupId: null } : r))));
    setDirty(true);
  };
  const assignRowToGroup = (rowId, gid) => {
    setRows(prev => reflowByGroups(prev.map(r => (r.id === rowId ? { ...r, groupId: gid || null } : r))));
    setDirty(true);
  };

  // Group lookup + tint helper. Alpha 0x22 (~13%) is subtle enough not
  // to overpower cell content but visible against the near-black cell bg.
  const colGroupById = useMemo(() => Object.fromEntries((colGroups || []).map(g => [g.id, g])), [colGroups]);
  const rowGroupById = useMemo(() => Object.fromEntries((rowGroups || []).map(g => [g.id, g])), [rowGroups]);
  const groupTint = (hex) => (hex ? hex + "22" : null);

  // Compute contiguous runs of same-groupId cols/rows so we can span a
  // single secondary header cell across each run. A divider that sits
  // BETWEEN two cells of the SAME group is absorbed INTO that run, so the
  // group stays ONE box with the separator drawn as a line inside it.
  // Dividers at a group boundary (or in ungrouped stretches) still break.
  const colGroupRuns = useMemo(() => {
    const runs = [];
    let curr = null;
    const nextGroupId = (idx) => {
      for (let j = idx + 1; j < cols.length; j++) {
        if (cols[j].isDivider) continue;
        return cols[j].groupId || null;
      }
      return null;
    };
    cols.forEach((col, idx) => {
      if (col.isDivider) {
        if (curr && !curr.isDivider && curr.groupId && nextGroupId(idx) === curr.groupId) {
          curr.cols.push(col); // interior divider — keep the group going
          return;
        }
        if (curr) { runs.push(curr); curr = null; }
        runs.push({ groupId: null, cols: [col], isDivider: true });
        return;
      }
      const gid = col.groupId || null;
      if (!curr || curr.groupId !== gid || curr.isDivider) {
        if (curr) runs.push(curr);
        curr = { groupId: gid, cols: [col], isDivider: false };
      } else {
        curr.cols.push(col);
      }
    });
    if (curr) runs.push(curr);
    return runs;
  }, [cols]);
  const rowGroupRuns = useMemo(() => {
    const runs = [];
    let curr = null;
    const nextGroupId = (idx) => {
      for (let j = idx + 1; j < rows.length; j++) {
        if (rows[j].isDivider) continue;
        return rows[j].groupId || null;
      }
      return null;
    };
    rows.forEach((row, idx) => {
      if (row.isDivider) {
        if (curr && !curr.isDivider && curr.groupId && nextGroupId(idx) === curr.groupId) {
          curr.rows.push(row); // interior divider — keep the group going
          return;
        }
        if (curr) { runs.push(curr); curr = null; }
        runs.push({ groupId: null, rows: [row], isDivider: true });
        return;
      }
      const gid = row.groupId || null;
      if (!curr || curr.groupId !== gid || curr.isDivider) {
        if (curr) runs.push(curr);
        curr = { groupId: gid, rows: [row], isDivider: false };
      } else {
        curr.rows.push(row);
      }
    });
    if (curr) runs.push(curr);
    return runs;
  }, [rows]);
  // Fast lookup: rowId → { start: bool (first row of its run), rowSpan: int, group } — used to
  // render the row group side-bar cell only on the FIRST row of each run
  // and to let the cell rowSpan the entire run vertically.
  const rowGroupPos = useMemo(() => {
    const map = {};
    rowGroupRuns.forEach(run => {
      if (run.isDivider) return;
      run.rows.forEach((r, i) => {
        map[r.id] = {
          start: i === 0,
          rowSpan: run.rows.length,
          groupId: run.groupId,  // may be null for ungrouped runs
        };
      });
    });
    return map;
  }, [rowGroupRuns]);

  // Column-group perimeter lookup: colId → { color, isFirst, isLast } — drives
  // the bright rounded border that wraps each contiguous column group.
  const colGroupEdge = useMemo(() => {
    const map = {};
    colGroupRuns.forEach(run => {
      if (run.isDivider || !run.groupId) return;
      const color = colGroupById[run.groupId]?.color;
      if (!color) return;
      run.cols.forEach((c, i) => {
        map[c.id] = { color, isFirst: i === 0, isLast: i === run.cols.length - 1 };
      });
    });
    return map;
  }, [colGroupRuns, colGroupById]);

  // Id of the last non-divider data row — the bottom edge of each column-group
  // border lands here so the rounded box closes cleanly.
  const lastGridRowId = useMemo(() => {
    const nd = (rows || []).filter(r => !r.isDivider);
    return nd.length ? nd[nd.length - 1].id : null;
  }, [rows]);

  // Row-group perimeter lookup: rowId → { color, isFirst, isLast } — the
  // horizontal twin of colGroupEdge. Drives the bright rounded border that
  // wraps each contiguous row group (side-bar → all columns of those rows).
  const rowGroupEdge = useMemo(() => {
    const map = {};
    rowGroupRuns.forEach(run => {
      if (run.isDivider || !run.groupId) return;
      const color = rowGroupById[run.groupId]?.color;
      if (!color) return;
      run.rows.forEach((r, i) => {
        map[r.id] = { color, isFirst: i === 0, isLast: i === run.rows.length - 1 };
      });
    });
    return map;
  }, [rowGroupRuns, rowGroupById]);

  // Id of the last non-divider column — the right edge of each row-group
  // border lands on this column's cell (or the row-total cell when present).
  const lastGridColId = useMemo(() => {
    const nd = (cols || []).filter(c => !c.isDivider);
    return nd.length ? nd[nd.length - 1].id : null;
  }, [cols]);

  // Row equivalent — mirrors the "change all cells in this column" flow
  // including the 3-way confirm dialog (overwrite / keep filled / cancel).
  const setRowType = useCallback((rowId, type) => {
    const filledKeys = [];
    cols.forEach(col => {
      if (col.isDivider) return;
      const cell = getCell(rowId, col.id);
      if (cell.type !== type && cellHasData(cell)) filledKeys.push(cellKey(rowId, col.id));
    });
    const apply = (mode) => {
      setCells(prev => {
        const next = { ...prev };
        cols.forEach(col => {
          if (col.isDivider) return;
          const key = cellKey(rowId, col.id);
          if (mode === "keep" && filledKeys.includes(key)) return;
          next[key] = { type, value: defaultCellValue(type) };
        });
        return next;
      });
      setDirty(true);
    };
    setRowTypeMenu(null);
    if (filledKeys.length === 0) { apply("overwrite"); return; }
    setTypeChangeConfirm({
      title: `Change row type to ${type}?`,
      message: `Some cells in this row already have data (${filledKeys.length}).`,
      targetType: type,
      filledCount: filledKeys.length,
      apply,
    });
  }, [cols, cells]);

  const applyDefaultTypeToAll = useCallback(() => {
    const filledKeys = [];
    rows.forEach(row => {
      if (row.isDivider) return;
      cols.forEach(col => {
        if (col.isDivider) return;
        const cell = getCell(row.id, col.id);
        if (cell.type !== defaultCellType && cellHasData(cell)) {
          filledKeys.push(cellKey(row.id, col.id));
        }
      });
    });
    const apply = (mode) => {
      setCells(prev => {
        const next = { ...prev };
        rows.forEach(row => {
          if (row.isDivider) return;
          cols.forEach(col => {
            if (col.isDivider) return;
            const key = cellKey(row.id, col.id);
            if (mode === "keep" && filledKeys.includes(key)) return;
            next[key] = {
              type: defaultCellType,
              value: defaultCellValue(defaultCellType),
            };
          });
        });
        return next;
      });
      setDirty(true);
    };
    if (filledKeys.length === 0) { apply("overwrite"); return; }
    setTypeChangeConfirm({
      title: `Change all cells to ${defaultCellType}?`,
      message: `Some cells in this grid already have data (${filledKeys.length}).`,
      targetType: defaultCellType,
      filledCount: filledKeys.length,
      apply,
    });
  }, [rows, cols, cells, defaultCellType]);

  // Bulk-convert only the EMPTY cells (no user-entered data) to the current
  // default type. Cells that already hold data keep their type. No confirm
  // needed since nothing is overwritten.
  const applyDefaultTypeToEmpty = useCallback(() => {
    setCells(prev => {
      const next = { ...prev };
      rows.forEach(row => {
        if (row.isDivider) return;
        cols.forEach(col => {
          if (col.isDivider) return;
          const key = cellKey(row.id, col.id);
          const cell = next[key] || { type: "checkbox", value: false };
          if (!cellHasData(cell)) {
            next[key] = { type: defaultCellType, value: defaultCellValue(defaultCellType) };
          }
        });
      });
      return next;
    });
    setDirty(true);
  }, [rows, cols, defaultCellType]);

  const getColumnType = useCallback((colId) => {
    const counts = { checkbox: 0, number: 0, symbol: 0, text: 0, character: 0, achievement: 0, note: 0, image: 0 };
    rows.forEach(row => {
      if (row.isDivider) return;
      const cell = getCell(row.id, colId);
      counts[cell.type] = (counts[cell.type] || 0) + 1;
    });
    // Return the most frequent type; fall back to checkbox.
    let winner = "checkbox";
    let best = counts.checkbox;
    for (const k of ["number", "symbol", "text", "character", "achievement", "note", "image"]) {
      if (counts[k] > best) { winner = k; best = counts[k]; }
    }
    return winner;
  }, [rows, cells]);

  // Same as getColumnType but along the ROW axis. Used for the per-row
  // "change this row's type" button so users can tile a whole row with
  // e.g. Image cells in one click. Ignores divider columns.
  const getRowType = useCallback((rowId) => {
    const counts = { checkbox: 0, number: 0, symbol: 0, text: 0, character: 0, achievement: 0, note: 0, image: 0 };
    cols.forEach(col => {
      if (col.isDivider) return;
      const cell = getCell(rowId, col.id);
      counts[cell.type] = (counts[cell.type] || 0) + 1;
    });
    let winner = "checkbox";
    let best = counts.checkbox;
    for (const k of ["number", "symbol", "text", "character", "achievement", "note", "image"]) {
      if (counts[k] > best) { winner = k; best = counts[k]; }
    }
    return winner;
  }, [cols, cells]);

  // True when any cell in this column is a text cell. A single text cell
  // opts the whole column out of the 64px character-column width cap so
  // long-form text can breathe.
  const columnHasText = useCallback((colId) => {
    return rows.some(row => !row.isDivider && getCell(row.id, colId).type === "text");
  }, [rows, cells]);

  // Per-cell character cap for Text cells. Chosen so the textarea (min-h 3rem,
  // text-xs ~12px line-height, ~4 lines visible) can display the entire value
  // without ever showing an internal scroll bar.
  const TEXT_CELL_MAX = 200;

  // Row operations
  const addRow = () => {
    const newRowId = crypto.randomUUID();
    setRows(prev => [...prev, { id: newRowId, name: "", color: "#a1a1aa" }]);
    // Seed cells in the new row using the grid's default cell type so
    // users don't have to convert them one-by-one after adding.
    if (defaultCellType && defaultCellType !== "checkbox") {
      setCells(prev => {
        const next = { ...prev };
        cols.forEach(col => {
          if (col.isDivider) return;
          next[`${newRowId}:${col.id}`] = { type: defaultCellType, value: defaultCellValue(defaultCellType) };
        });
        return next;
      });
    }
    setDirty(true);
  };
  const addDividerRow = () => {
    setRows(prev => [...prev, { id: crypto.randomUUID(), name: "", color: "#a1a1aa", isDivider: true }]);
    setDirty(true);
  };
  const removeRow = (rowId) => {
    setRows(prev => prev.filter(r => r.id !== rowId));
    setCells(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { if (k.startsWith(rowId + ":")) delete next[k]; });
      return next;
    });
    setDirty(true);
  };
  const updateRow = (rowId, field, value) => {
    setRows(prev => prev.map(r => r.id === rowId ? { ...r, [field]: value } : r));
    setDirty(true);
  };

  // Col operations
  const addCol = () => {
    const newColId = crypto.randomUUID();
    setCols(prev => [...prev, { id: newColId, name: "" }]);
    // Seed cells in the new column using the grid's default cell type.
    if (defaultCellType && defaultCellType !== "checkbox") {
      setCells(prev => {
        const next = { ...prev };
        rows.forEach(row => {
          if (row.isDivider) return;
          next[`${row.id}:${newColId}`] = { type: defaultCellType, value: defaultCellValue(defaultCellType) };
        });
        return next;
      });
    }
    setDirty(true);
  };
  const addDividerCol = () => {
    setCols(prev => [...prev, { id: crypto.randomUUID(), name: "", isDivider: true, color: "#a1a1aa" }]);
    setDirty(true);
  };
  const removeCol = (colId) => {
    setCols(prev => prev.filter(c => c.id !== colId));
    setCells(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(k => { if (k.endsWith(":" + colId)) delete next[k]; });
      return next;
    });
    setDirty(true);
  };
  const updateCol = (colId, field, value) => {
    setCols(prev => prev.map(c => c.id === colId ? { ...c, [field]: value } : c));
    setDirty(true);
  };

  // Column totals for number cells
  const colTotals = useMemo(() => {
    const totals = {};
    cols.forEach(col => {
      if (col.isDivider) { totals[col.id] = null; return; }
      let sum = 0;
      let hasNumbers = false;
      rows.forEach(row => {
        if (row.isDivider) return;
        const cell = getCell(row.id, col.id);
        if (cell.type === "number" && typeof cell.value === "number") {
          sum += cell.value;
          hasNumbers = true;
        }
      });
      totals[col.id] = hasNumbers ? sum : null;
    });
    return totals;
  }, [cells, rows, cols]);

  // Row totals for number cells
  const rowTotals = useMemo(() => {
    const totals = {};
    rows.forEach(row => {
      if (row.isDivider) { totals[row.id] = null; return; }
      let sum = 0;
      let hasNumbers = false;
      cols.forEach(col => {
        if (col.isDivider) return;
        const cell = getCell(row.id, col.id);
        if (cell.type === "number" && typeof cell.value === "number") {
          sum += cell.value;
          hasNumbers = true;
        }
      });
      totals[row.id] = hasNumbers ? sum : null;
    });
    return totals;
  }, [cells, rows, cols]);

  const hasRowTotals = rows.some(r => !r.isDivider && rowTotals[r.id] !== null);

  // Green-check counts per row / per column (Marks On mode feature).
  const rowCheckCounts = useMemo(() => {
    const m = {};
    rows.forEach(row => {
      if (row.isDivider) { m[row.id] = 0; return; }
      let n = 0;
      cols.forEach(col => {
        if (col.isDivider) return;
        if (cellMarks[`${row.id}:${col.id}`] === "check") n++;
      });
      m[row.id] = n;
    });
    return m;
  }, [cellMarks, rows, cols]);
  const colCheckCounts = useMemo(() => {
    const m = {};
    cols.forEach(col => {
      if (col.isDivider) { m[col.id] = 0; return; }
      let n = 0;
      rows.forEach(row => {
        if (row.isDivider) return;
        if (cellMarks[`${row.id}:${col.id}`] === "check") n++;
      });
      m[col.id] = n;
    });
    return m;
  }, [cellMarks, rows, cols]);
  // Trailing count column/row is only active in Marks mode with the toggle on.
  const countsActive = marksMode && showCounts;

  const handleSave = () => {
    onSave({ name, rows, cols, cells, zoom, cellMarks, fromTemplateId: fromTemplateId || "", defaultCellType, rowGroups, colGroups, showCounts, corner: (corner && (corner.text || corner.entityType || corner.imageUrl || corner.borderColor)) ? corner : null });
    setDirty(false);
  };

  // Cycle a cell's mark: "" -> "check" -> "x" -> "" (removes).
  // Used both by Marks mode (any grid) and by template-locked grids.
  const cycleCellMark = (rowId, colId) => {
    const key = cellKey(rowId, colId);
    setCellMarks(prev => {
      const cur = prev[key] || "";
      const next = cur === "" ? "check" : cur === "check" ? "x" : "";
      const out = { ...prev };
      if (next) out[key] = next; else delete out[key];
      return out;
    });
    setDirty(true);
  };

  const handleCellClick = (rowId, colId) => {
    // In Marks mode (default for template-based grids, or user-toggled on
    // any grid) a click always cycles the check/X overlay instead of
    // opening the cell's type-specific picker.
    if (marksMode) {
      cycleCellMark(rowId, colId);
      return;
    }
    const cell = getCell(rowId, colId);
    if (cell.type === "checkbox") {
      setCell(rowId, colId, { value: !cell.value });
    } else if (cell.type === "symbol") {
      setSymbolPicker(`${rowId}:${colId}`);
    } else if (cell.type === "character") {
      setCharacterPicker(`${rowId}:${colId}`);
    } else if (cell.type === "achievement") {
      setAchievementPicker(`${rowId}:${colId}`);
    } else if (cell.type === "note") {
      setNotePicker(`${rowId}:${colId}`);
    }
  };

  const dismissMenus = () => {
    setCellTypeMenu(null);
    setColTypeMenu(null);
    setSymbolPicker(null);
    setCharacterPicker(null);
    setAchievementPicker(null);
    setNotePicker(null);
    setImagePicker(null);
    setAnchorPicker(null);
    setColorPicker(null);
  };

  // Row/col anchoring — attach a character or achievement to a header.
  // When anchored, cells in that row/col can no longer be that entity type.
  const anchorRow = (rowId, kind, entity) => {
    // `kind` is character | achievement | note | image.
    // For image, `entity` is `{ imageUrl }` (raw URL); for note, `entity` is
    // a note doc (with imageUrl + title); characters/achievements use the
    // existing full entity object.
    let img = null;
    let entityName = "";
    let entityId = null;
    if (kind === "character") {
      img = (entity.images && entity.images[0]) || null;
      entityName = entity.title || "Unnamed";
      entityId = entity.id;
    } else if (kind === "achievement") {
      img = entity.imageUrl || null;
      entityName = entity.name || "Unnamed";
      entityId = entity.id;
    } else if (kind === "note") {
      img = entity.imageUrl || null;
      entityName = entity.title || entity.text?.slice(0, 40) || "Note";
      entityId = entity.id;
    } else if (kind === "image") {
      img = entity.imageUrl || "";
      entityName = "";
      entityId = null;
    }
    setRows(prev => prev.map(r => r.id === rowId ? {
      ...r,
      entityType: kind,
      entityId,
      entityName,
      entityImageUrl: img,
    } : r));
    setDirty(true);
    // If any cell in this row was already of the same entity type, reset it
    // to checkbox so the "no duplicate entity in anchored row" rule holds.
    setCells(prev => {
      const next = { ...prev };
      cols.forEach(col => {
        if (col.isDivider) return;
        const key = cellKey(rowId, col.id);
        if (next[key]?.type === kind) {
          next[key] = { type: "checkbox", value: false };
        }
      });
      return next;
    });
  };
  const clearRowAnchor = (rowId) => {
    setRows(prev => prev.map(r => r.id === rowId ? {
      ...r,
      entityType: undefined,
      entityId: undefined,
      entityName: undefined,
      entityImageUrl: undefined,
    } : r));
    setDirty(true);
  };
  // Optional top-left corner content — a short description + one linked
  // character note / regular note / image.
  const setCornerText = (text) => { setCorner(prev => ({ ...(prev || {}), text })); setDirty(true); };
  const anchorCorner = (kind, entity) => {
    let img = null, entityName = "", entityId = null;
    if (kind === "character") { img = (entity.images && entity.images[0]) || null; entityName = entity.title || "Unnamed"; entityId = entity.id; }
    else if (kind === "note") { img = entity.imageUrl || null; entityName = entity.title || entity.text?.slice(0, 40) || "Note"; entityId = entity.id; }
    else if (kind === "achievement") { img = entity.imageUrl || null; entityName = entity.name || "Unnamed"; entityId = entity.id; }
    else if (kind === "image") { img = entity.imageUrl || ""; }
    setCorner(prev => ({ ...(prev || {}), entityType: kind, entityId, entityName, entityImageUrl: img, imageUrl: kind === "image" ? img : null }));
    setDirty(true);
  };
  const clearCornerAnchor = () => {
    setCorner(prev => prev ? ({ ...prev, entityType: null, entityId: null, entityName: null, entityImageUrl: null, imageUrl: null }) : prev);
    setDirty(true);
  };
  const anchorCol = (colId, kind, entity) => {
    let img = null;
    let entityName = "";
    let entityId = null;
    if (kind === "character") {
      img = (entity.images && entity.images[0]) || null;
      entityName = entity.title || "Unnamed";
      entityId = entity.id;
    } else if (kind === "achievement") {
      img = entity.imageUrl || null;
      entityName = entity.name || "Unnamed";
      entityId = entity.id;
    } else if (kind === "note") {
      img = entity.imageUrl || null;
      entityName = entity.title || entity.text?.slice(0, 40) || "Note";
      entityId = entity.id;
    } else if (kind === "image") {
      img = entity.imageUrl || "";
      entityName = "";
      entityId = null;
    }
    setCols(prev => prev.map(c => c.id === colId ? {
      ...c,
      entityType: kind,
      entityId,
      entityName,
      entityImageUrl: img,
    } : c));
    setDirty(true);
    setCells(prev => {
      const next = { ...prev };
      rows.forEach(row => {
        if (row.isDivider) return;
        const key = cellKey(row.id, colId);
        if (next[key]?.type === kind) {
          next[key] = { type: "checkbox", value: false };
        }
      });
      return next;
    });
  };
  const clearColAnchor = (colId) => {
    setCols(prev => prev.map(c => c.id === colId ? {
      ...c,
      entityType: undefined,
      entityId: undefined,
      entityName: undefined,
      entityImageUrl: undefined,
    } : c));
    setDirty(true);
  };

  // Which cell types are still allowed for a specific cell, given the
  // anchor state of its row and column.
  const allowedCellTypes = useCallback((rowId, colId) => {
    const row = rows.find(r => r.id === rowId);
    const col = cols.find(c => c.id === colId);
    const banned = new Set();
    if (row?.entityType) banned.add(row.entityType);
    if (col?.entityType) banned.add(col.entityType);
    return Object.keys(CELL_TYPES).filter(t => !banned.has(t));
  }, [rows, cols]);

  // Rotate column headers 45° once we have enough data columns to save
  // horizontal space and keep the whole grid on one screen.
  const dataColCount = cols.filter(c => !c.isDivider).length;
  const rotateHeaders = dataColCount > ROTATE_HEADER_THRESHOLD;

  // ============ Drag-and-drop reordering for rows and columns ============
  // Each drag has two pieces of state:
  //   drag*Id      — id of the row/col currently being dragged
  //   drop*Target  — { targetId, position: 'before'|'after' } used to render
  //                  the cyan drop-line and to compute the insert index
  const [dragRowId, setDragRowId] = useState(null);
  const [dropRowTarget, setDropRowTarget] = useState(null);
  const [dragColId, setDragColId] = useState(null);
  const [dropColTarget, setDropColTarget] = useState(null);

  const clearRowDrag = () => { setDragRowId(null); setDropRowTarget(null); };
  const clearColDrag = () => { setDragColId(null); setDropColTarget(null); };

  const reorderRows = (srcId, targetId, position) => {
    setRows(prev => {
      const srcIdx = prev.findIndex(r => r.id === srcId);
      if (srcIdx === -1 || srcId === targetId) return prev;
      const src = prev[srcIdx];
      const without = prev.filter(r => r.id !== srcId);
      const tgtIdx = without.findIndex(r => r.id === targetId);
      if (tgtIdx === -1) return prev;
      const insertAt = position === "before" ? tgtIdx : tgtIdx + 1;
      // If we're dropping the row between two neighbors that both live in
      // the same group, adopt that group so the moved row visually joins
      // it (matches user spec: "adds the column/row to the group").
      const leftGid = without[insertAt - 1]?.groupId;
      const rightGid = without[insertAt]?.groupId;
      let placed = src;
      if (leftGid && leftGid === rightGid && leftGid !== src.groupId) {
        placed = { ...src, groupId: leftGid };
      }
      return [...without.slice(0, insertAt), placed, ...without.slice(insertAt)];
    });
    setDirty(true);
  };

  const reorderCols = (srcId, targetId, position) => {
    setCols(prev => {
      const srcIdx = prev.findIndex(c => c.id === srcId);
      if (srcIdx === -1 || srcId === targetId) return prev;
      const src = prev[srcIdx];
      const without = prev.filter(c => c.id !== srcId);
      const tgtIdx = without.findIndex(c => c.id === targetId);
      if (tgtIdx === -1) return prev;
      const insertAt = position === "before" ? tgtIdx : tgtIdx + 1;
      const leftGid = without[insertAt - 1]?.groupId;
      const rightGid = without[insertAt]?.groupId;
      let placed = src;
      if (leftGid && leftGid === rightGid && leftGid !== src.groupId) {
        placed = { ...src, groupId: leftGid };
      }
      return [...without.slice(0, insertAt), placed, ...without.slice(insertAt)];
    });
    setDirty(true);
  };

  // Row header/tr drag handlers. Use clientY to decide before/after so the
  // drop-line snaps to the closer edge as the user hovers within a row.
  const onRowDragOver = (targetId) => (e) => {
    if (!dragRowId || dragRowId === targetId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const position = e.clientY < midY ? "before" : "after";
    if (dropRowTarget?.targetId !== targetId || dropRowTarget?.position !== position) {
      setDropRowTarget({ targetId, position });
    }
  };
  const onRowDrop = (e) => {
    e.preventDefault();
    if (dragRowId && dropRowTarget) {
      reorderRows(dragRowId, dropRowTarget.targetId, dropRowTarget.position);
    }
    clearRowDrag();
  };

  const onColDragOver = (targetId) => (e) => {
    if (!dragColId || dragColId === targetId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    const rect = e.currentTarget.getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    const position = e.clientX < midX ? "before" : "after";
    if (dropColTarget?.targetId !== targetId || dropColTarget?.position !== position) {
      setDropColTarget({ targetId, position });
    }
  };
  const onColDrop = (e) => {
    e.preventDefault();
    if (dragColId && dropColTarget) {
      reorderCols(dragColId, dropColTarget.targetId, dropColTarget.position);
    }
    clearColDrag();
  };

  // Class helpers for the cyan drop-line. We use `inset` box-shadows on each
  // td/th so the line spans the full width of a row or the full height of a
  // column even though those aren't real DOM elements in an HTML table.
  const rowDropClass = (rowId) => {
    if (dropRowTarget?.targetId !== rowId) return "";
    return dropRowTarget.position === "before"
      ? "shadow-[inset_0_3px_0_0_rgb(34,211,238)]"
      : "shadow-[inset_0_-3px_0_0_rgb(34,211,238)]";
  };
  const colDropClass = (colId) => {
    if (dropColTarget?.targetId !== colId) return "";
    return dropColTarget.position === "before"
      ? "shadow-[inset_3px_0_0_0_rgb(34,211,238)]"
      : "shadow-[inset_-3px_0_0_0_rgb(34,211,238)]";
  };

  // Row/col index for placeholders
  const rowIdx = (rowId) => {
    const dataRows = rows.filter(r => !r.isDivider);
    const i = dataRows.findIndex(r => r.id === rowId);
    return i >= 0 ? i + 1 : "";
  };
  const colIdx = (colId) => {
    const dataCols = cols.filter(c => !c.isDivider);
    const i = dataCols.findIndex(c => c.id === colId);
    return i >= 0 ? i + 1 : "";
  };

  // Measure the column-group header row so the main column-title row can be
  // pinned directly beneath it (both sticky) instead of overlapping at top:0.
  const colGroupRowRef = useRef(null);
  const [colGroupRowH, setColGroupRowH] = useState(0);
  useLayoutEffect(() => {
    if (colGroups.length > 0 && colGroupRowRef.current) {
      setColGroupRowH(colGroupRowRef.current.getBoundingClientRect().height);
    } else {
      setColGroupRowH(0);
    }
  }, [colGroups.length, cols, rows, marksMode, name, rowLabelColWidthRem, rotatedHeaderHeightRem]);

  return (
    <div className="space-y-3" data-testid="scorecard-editor">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Input
          value={name}
          onChange={e => { setName(e.target.value); setDirty(true); }}
          className="bg-zinc-800/50 border-zinc-700 text-white font-semibold text-sm h-8 flex-1"
          placeholder="Grid name"
          data-testid="scorecard-name-input"
        />
        {!marksMode && (
          <>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={addCol} className="text-zinc-400 hover:text-white h-8 px-2" data-testid="add-col-btn" title="Add column">
                <Plus className="w-3 h-3 mr-1" /> Col
              </Button>
              <Button size="sm" variant="ghost" onClick={addDividerCol} className="text-zinc-500 hover:text-white h-8 px-1.5" data-testid="add-divider-col-btn" title="Add divider column">
                <Minus className="w-3 h-3" />
              </Button>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" onClick={addRow} className="text-zinc-400 hover:text-white h-8 px-2" data-testid="add-row-btn" title="Add row">
                <Plus className="w-3 h-3 mr-1" /> Row
              </Button>
              <Button size="sm" variant="ghost" onClick={addDividerRow} className="text-zinc-500 hover:text-white h-8 px-1.5" data-testid="add-divider-row-btn" title="Add divider row">
                <Minus className="w-3 h-3" />
              </Button>
            </div>
          </>
        )}
      </div>

      {/* Grid. Wrapper allows both axes to scroll while row/column headers
          stay pinned via `position: sticky`. Corner cell (top-left) gets
          the highest z-index so it stays on top when both axes scroll. */}
      <div className="overflow-auto max-h-[70vh] border border-white/10 rounded scorecard-sticky-wrap" style={{ "--main-header-top": `${colGroupRowH}px` }}>
        <style>{`
          /* All column headers pin to the top; the first cell in every row
             (the row label) pins to the left. Solid backgrounds prevent
             underlying rows/cells from showing through when scrolling. */
          .scorecard-sticky-wrap thead th {
            position: sticky;
            top: 0;
            z-index: 20;
            background: #0A0A0A;
          }
          /* Column-title row sits directly BELOW the sticky column-group
             bar (its measured height) so both stay pinned when scrolling,
             instead of overlapping at top:0. */
          .scorecard-sticky-wrap thead tr.scorecard-main-header > th {
            top: var(--main-header-top, 0px);
          }
          /* The group bar row must paint above the column-title row. */
          .scorecard-sticky-wrap thead tr.scorecard-col-group-row > th {
            z-index: 21;
          }
          .scorecard-sticky-wrap tbody > tr > td.sc-sticky-left,
          .scorecard-sticky-wrap tbody > tr > th.sc-sticky-left {
            position: sticky;
            z-index: 10;
            background-color: #0A0A0A;
          }
          /* Header text always allowed to wrap so tall labels don't get truncated. */
          .scorecard-sticky-wrap thead th input,
          .scorecard-sticky-wrap thead th .scorecard-header-text {
            white-space: normal;
            overflow-wrap: anywhere;
          }
        `}</style>
        <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
          <thead>
            {colGroups.length > 0 && (
              // Secondary column-group header. One <th> per RUN of
              // consecutive same-group cols; ungrouped runs render an
              // empty spacer so the total colSpan matches the main tr.
              // Row-group column (if any) needs a leading placeholder
              // <th> so alignment stays correct.
              <tr data-testid="col-group-header-row" ref={colGroupRowRef} className="scorecard-col-group-row">
                {rowGroups.length > 0 && <th className="w-[1.05rem] min-w-[1.05rem]" style={{ left: 0, zIndex: 22 }} />}
                <th style={{ width: `${rowLabelColWidthRem}rem`, minWidth: `${rowLabelColWidthRem}rem`, left: rowGroups.length > 0 ? "1.05rem" : 0, zIndex: 22 }} />
                {colGroupRuns.map((run) => {
                  const g = run.groupId ? colGroupById[run.groupId] : null;
                  return (
                    <th
                      key={run.cols[0].id + ":cg"}
                      colSpan={run.cols.length}
                      className="px-1 py-0.5 align-middle"
                      style={g ? {
                        borderTop: `2px solid ${g.color}`,
                        borderLeft: `2px solid ${g.color}`,
                        borderRight: `2px solid ${g.color}`,
                        borderTopLeftRadius: "8px",
                        borderTopRightRadius: "8px",
                      } : undefined}
                    >
                      {g && (
                        <div
                          className="text-[11px] font-medium px-2 py-0.5 rounded text-center truncate"
                          style={{
                            background: g.color + "33",
                            color: "#fff",
                            border: `1px solid ${g.color}77`,
                          }}
                          title={g.name}
                          data-testid={`col-group-bar-${g.id}`}
                        >
                          {g.name}
                        </div>
                      )}
                    </th>
                  );
                })}
                {hasRowTotals && <th />}
                {countsActive && <th />}
              </tr>
            )}
            <tr className="scorecard-main-header">
              {rowGroups.length > 0 && (
                // Column reserved for the row-group side bar. Empty here
                // (labels live inline with the data rows).
                <th className="w-[1.05rem] min-w-[1.05rem]" style={{ zIndex: 30, left: 0 }} />
              )}
              <th
                className="align-top p-0"
                style={{
                  boxShadow: "1px 1px 0 rgb(255 255 255 / 0.06)",
                  // Above the rotated column headers (z up to ~48) so their
                  // slanted labels never paint over the corner description.
                  zIndex: 49,
                  left: rowGroups.length > 0 ? "1.05rem" : 0,
                  width: `${rowLabelColWidthRem}rem`,
                  minWidth: `${rowLabelColWidthRem}rem`,
                  // Border wraps the WHOLE corner cell (full width + full
                  // header-row height), not just the text block. Tint is
                  // layered over an OPAQUE base so scrolled rows/cols never
                  // bleed through the sticky corner.
                  ...(corner?.borderColor
                    ? { border: `2px solid ${corner.borderColor}`, borderRadius: "8px", background: `linear-gradient(${corner.borderColor}14, ${corner.borderColor}14), #0A0A0A` }
                    : {}),
                }}
                data-testid="grid-corner-cell"
              >
                {(() => {
                  const hasContent = corner && (corner.text || corner.entityType || corner.imageUrl || corner.borderColor);
                  const trigger = (
                    <div
                      className={`relative w-full h-full min-h-[2.25rem] p-1.5 flex flex-col gap-1 items-start justify-start text-left group/corner ${marksMode ? "" : "cursor-pointer hover:bg-white/5"}`}
                      title={marksMode ? undefined : "Click to edit grid description"}
                      data-testid="grid-corner-trigger"
                    >
                      {!marksMode && (
                        <Pencil className="w-3 h-3 text-zinc-500 absolute top-1 right-1 opacity-0 group-hover/corner:opacity-100 transition-opacity" data-testid="grid-corner-edit-hint" />
                      )}
                      {corner?.entityType && (
                        <ColRowEntityDisplay
                          entity={resolveAnchorEntity(corner.entityType, corner.entityId)}
                          fallback={{ name: corner.entityName, imageUrl: corner.entityImageUrl }}
                          kind={corner.entityType}
                          inRow
                          imgMaxPx={imgMaxPx}
                        />
                      )}
                      {corner?.imageUrl && !corner?.entityType && (
                        <NoteHover value={{ title: corner.text || "Image", imageUrl: corner.imageUrl }}>
                          <img src={corner.imageUrl} alt="" className="w-9 h-9 rounded object-cover border border-white/10" />
                        </NoteHover>
                      )}
                      {corner?.text ? (
                        <div className="text-[11px] font-normal text-zinc-300 leading-snug whitespace-normal break-words">{corner.text}</div>
                      ) : !hasContent && !marksMode ? (
                        <div className="text-[10px] font-normal text-zinc-600 italic group-hover/corner:text-zinc-400">+ Describe grid</div>
                      ) : null}
                    </div>
                  );
                  if (marksMode) return hasContent ? trigger : <div className="min-h-[1rem]" />;
                  const d = cornerDraft || {};
                  const setD = (patch) => setCornerDraft(prev => ({ ...(prev || {}), ...patch }));
                  const draftAnchor = (kind, entity) => {
                    let img = null, entityName = "", entityId = null;
                    if (kind === "character") { img = (entity.images && entity.images[0]) || null; entityName = entity.title || "Unnamed"; entityId = entity.id; }
                    else if (kind === "note") { img = entity.imageUrl || null; entityName = entity.title || entity.text?.slice(0, 40) || "Note"; entityId = entity.id; }
                    else if (kind === "achievement") { img = entity.imageUrl || null; entityName = entity.name || "Unnamed"; entityId = entity.id; }
                    else if (kind === "image") { img = entity.imageUrl || ""; }
                    setD({ entityType: kind, entityId, entityName, entityImageUrl: img, imageUrl: kind === "image" ? img : null });
                  };
                  return (
                    <Popover
                      open={cornerOpen}
                      onOpenChange={(o) => { if (o) { setCornerDraft(corner ? { ...corner } : {}); setCornerImgInput(""); } setCornerOpen(o); }}
                    >
                      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
                      <PopoverContent side="bottom" align="start" className="w-72 bg-zinc-900 border-zinc-700 p-3 space-y-2" data-testid="grid-corner-editor">
                        <div className="text-[11px] font-semibold text-zinc-300">Grid description</div>
                        <textarea
                          value={d.text || ""}
                          onChange={(e) => setD({ text: e.target.value })}
                          placeholder="Describe what this grid is for..."
                          rows={4}
                          maxLength={280}
                          className="w-full text-xs bg-zinc-800 border border-zinc-700 rounded p-2 text-zinc-200 resize-none focus:outline-none focus:border-teal-500"
                          data-testid="grid-corner-text"
                        />
                        <div className="text-[10px] text-zinc-500 text-right -mt-1">{(d.text || "").length}/280</div>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[11px] text-zinc-400 mr-1">Border:</span>
                          <button
                            onClick={() => setD({ borderColor: null })}
                            className={`w-4 h-4 rounded-sm border border-white/20 flex items-center justify-center ${!d.borderColor ? "ring-1 ring-white/70" : ""}`}
                            title="No border"
                            data-testid="grid-corner-border-none"
                          >
                            <X className="w-2.5 h-2.5 text-zinc-500" />
                          </button>
                          {ROW_COLORS.map(cc => (
                            <button
                              key={cc}
                              onClick={() => setD({ borderColor: cc })}
                              className={`w-4 h-4 rounded-sm border border-white/20 hover:scale-110 transition-transform ${d.borderColor === cc ? "ring-1 ring-white/80" : ""}`}
                              style={{ backgroundColor: cc }}
                              data-testid={`grid-corner-border-${cc}`}
                            />
                          ))}
                        </div>
                        <div className="text-[11px] text-zinc-400">Attach (optional):</div>
                        {d.entityType || d.imageUrl ? (
                          <div className="flex items-center justify-between gap-2 bg-zinc-800 rounded p-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                              {(d.entityImageUrl || d.imageUrl) && (
                                <img src={d.entityImageUrl || d.imageUrl} className="w-8 h-8 rounded object-cover" alt="" />
                              )}
                              <span className="text-xs text-zinc-300 truncate">{d.entityType ? (d.entityName || d.entityType) : "Image link"}</span>
                            </div>
                            <button onClick={() => setD({ entityType: null, entityId: null, entityName: null, entityImageUrl: null, imageUrl: null })} className="text-zinc-500 hover:text-red-400 flex-shrink-0" data-testid="grid-corner-clear-anchor">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <AnchorPickerMenu
                              characters={characters}
                              achievements={[]}
                              notes={notes}
                              onSelect={(kind, entity) => draftAnchor(kind, entity)}
                              onClose={() => {}}
                            />
                            <div className="flex items-center gap-1 pt-1">
                              <input
                                value={cornerImgInput}
                                onChange={(e) => setCornerImgInput(e.target.value)}
                                placeholder="Or paste an image URL"
                                className="flex-1 text-xs bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-zinc-200 focus:outline-none focus:border-teal-500"
                                data-testid="grid-corner-image-input"
                              />
                              <button
                                onClick={() => { if (cornerImgInput.trim()) { draftAnchor("image", { imageUrl: cornerImgInput.trim() }); setCornerImgInput(""); } }}
                                className="text-xs px-2 py-1 rounded bg-teal-600 hover:bg-teal-500 text-white flex-shrink-0"
                                data-testid="grid-corner-image-add"
                              >Add</button>
                            </div>
                          </>
                        )}
                        <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800">
                          <button
                            onClick={() => { setCornerDraft(null); setCornerImgInput(""); setCornerOpen(false); }}
                            className="text-xs px-3 py-1 rounded border border-zinc-600 text-zinc-300 hover:bg-zinc-800"
                            data-testid="grid-corner-cancel"
                          >Cancel</button>
                          <button
                            onClick={() => {
                              const has = d && (d.text || d.entityType || d.imageUrl || d.borderColor);
                              setCorner(has ? { ...d } : null);
                              setDirty(true);
                              setCornerDraft(null);
                              setCornerImgInput("");
                              setCornerOpen(false);
                            }}
                            className="text-xs px-3 py-1 rounded bg-teal-600 hover:bg-teal-500 text-white"
                            data-testid="grid-corner-ok"
                          >Okay</button>
                        </div>
                      </PopoverContent>
                    </Popover>
                  );
                })()}
              </th>
              {cols.map(col => {
                if (col.isDivider) {
                  return (
                    <th
                      key={col.id}
                      className={`group/col relative px-0 py-0 w-2 min-w-[0.5rem] ${colDropClass(col.id)} ${dragColId === col.id ? "opacity-40" : ""}`}
                      // Sit ABOVE the rotated column headers (which use
                      // z-index 21–45) so the divider's color swatch and
                      // remove button remain visible and clickable when
                      // a long neighboring header rotates over the top of
                      // the narrow divider column.
                      style={{ zIndex: 46 }}
                      onDragOver={onColDragOver(col.id)}
                      onDrop={onColDrop}
                      onDragLeave={(e) => {
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                          if (dropColTarget?.targetId === col.id) setDropColTarget(null);
                        }
                      }}
                    >
                      {/* Continuous separator line — runs the full header
                          height so it connects seamlessly with the divider
                          line down through every data row. */}
                      <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px]" style={{ backgroundColor: col.color || "#3f3f46" }} />
                      {!marksMode && (
                      <div
                        className="relative flex flex-col items-center gap-0.5 pt-1"
                        draggable
                        onDragStart={(e) => {
                          setDragColId(col.id);
                          e.dataTransfer.effectAllowed = "move";
                          e.dataTransfer.setData("text/plain", `col:${col.id}`);
                        }}
                        onDragEnd={clearColDrag}
                        title="Drag to reorder this divider"
                      >
                        <Popover
                          open={colorPicker === `col-${col.id}`}
                          onOpenChange={(o) => setColorPicker(o ? `col-${col.id}` : null)}
                        >
                          <PopoverTrigger asChild>
                            <button
                              className="w-2 h-4 rounded-sm border border-white/10"
                              style={{ backgroundColor: col.color || "#3f3f46" }}
                            />
                          </PopoverTrigger>
                          <PopoverContent side="bottom" align="center" className={`${POPOVER_MENU_CN} p-1.5`}>
                            <div className="flex flex-wrap gap-1 w-[160px]">
                              {ROW_COLORS.map(c => (
                                <button
                                  key={c}
                                  onClick={() => { updateCol(col.id, "color", c); setColorPicker(null); }}
                                  className="w-4 h-4 rounded-sm border border-white/20 hover:scale-110 transition-transform"
                                  style={{ backgroundColor: c }}
                                />
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        <button
                          onClick={() => removeCol(col.id)}
                          className="text-zinc-700 hover:text-red-400 opacity-0 group-hover/col:opacity-100 transition-opacity"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      </div>
                      )}
                    </th>
                  );
                }
                const colType = getColumnType(col.id);
                const TypeIcon = CELL_TYPES[colType].icon;
                // Character/image/note columns cap out at the current image
                // size (64px or 32px based on zoom) UNLESS the column also
                // contains a text cell — in that case the text needs room to
                // breathe and the cap is dropped. Anchored columns also drop
                // the cap so the entity label has room. Rotated headers use
                // an even tighter min-width when zoomed so columns pack close.
                const hasText = columnHasText(col.id);
                const isImageLikeCol = colType === "character" || colType === "image" || colType === "note";
                const zoomedCap = imgMaxPx === 16 ? "w-[20px] min-w-[20px] max-w-[20px]" : imgMaxPx === 32 ? "w-[36px] min-w-[36px] max-w-[36px]" : "w-[64px] min-w-[64px] max-w-[64px]";
                const rotatedMin = imgMaxPx === 16 ? "min-w-[1.5rem] max-w-[1.5rem]" : imgMaxPx === 32 ? "min-w-[2rem] max-w-[2rem]" : "min-w-[2.5rem] max-w-[2.5rem]";
                const colWidthClass = (isImageLikeCol && !hasText && !col.entityType)
                  ? zoomedCap
                  : rotateHeaders
                    ? rotatedMin
                    : "min-w-[4.5rem]";
                const gEdge = colGroupEdge[col.id];
                const gTint = col.groupId ? colGroupById[col.groupId]?.color : null;
                return (
                  <th
                    key={col.id}
                    className={`group/col px-1 py-1 ${colWidthClass} align-bottom ${colDropClass(col.id)} ${dragColId === col.id ? "opacity-40" : ""}`}
                    // Reversed z-index so the LEFTMOST column's slanted
                    // label paints on top of its neighbors to the right.
                    // Range 21-49 stays STRICTLY BELOW Radix popover
                    // z-50 so cell-picker dialogs are never occluded.
                    // Grids with more than 28 columns will share z-index
                    // 21 beyond that point (DOM order handles overlap).
                    style={{
                      zIndex: Math.max(21, 49 - colIdx(col.id)),
                      // Opaque dark base so grid content never bleeds
                      // through the sticky header when scrolled; group
                      // tint is layered on top via a gradient overlay.
                      backgroundColor: "#0A0A0A",
                      backgroundImage: gTint ? `linear-gradient(${gTint}1f, ${gTint}1f)` : undefined,
                      borderLeft: gEdge?.isFirst ? `2px solid ${gEdge.color}` : undefined,
                      borderRight: gEdge?.isLast ? `2px solid ${gEdge.color}` : undefined,
                    }}
                    onDragOver={onColDragOver(col.id)}
                    onDrop={onColDrop}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        if (dropColTarget?.targetId === col.id) setDropColTarget(null);
                      }
                    }}
                  >
                    {!marksMode && (
                    <div className="relative flex flex-col items-center gap-0.5 mb-1">
                      <Popover
                        open={colorPicker === `col-${col.id}`}
                        onOpenChange={(o) => setColorPicker(o ? `col-${col.id}` : null)}
                      >
                        <PopoverTrigger asChild>
                          <button
                            draggable
                            onDragStart={(e) => { setDragColId(col.id); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", `col:${col.id}`); }}
                            onDragEnd={clearColDrag}
                            className="w-3.5 h-3.5 rounded-sm border border-white/20 cursor-grab active:cursor-grabbing hover:ring-1 hover:ring-white/50 transition"
                            style={{ backgroundColor: col.color || "#52525b" }}
                            title="Drag to reorder · click to set color"
                            data-testid={`col-handle-${col.id}`}
                          />
                        </PopoverTrigger>
                        <PopoverContent side="bottom" align="center" className={`${POPOVER_MENU_CN} p-1.5`}>
                          <div className="flex flex-wrap gap-1 w-[160px]">
                            {ROW_COLORS.map(c => (
                              <button
                                key={c}
                                onClick={() => { updateCol(col.id, "color", c); setColorPicker(null); }}
                                className="w-4 h-4 rounded-sm border border-white/20 hover:scale-110 transition-transform"
                                style={{ backgroundColor: c }}
                              />
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      <Popover
                        open={colTypeMenu === col.id}
                        onOpenChange={(o) => setColTypeMenu(o ? col.id : null)}
                      >
                        <PopoverTrigger asChild>
                          <button
                            className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                              CELL_TYPES[colType].color
                            } bg-zinc-800/80 border-zinc-700 hover:border-zinc-500`}
                            title={`Column type: ${CELL_TYPES[colType].label}. Click to change.`}
                            data-testid={`col-type-btn-${col.id}`}
                          >
                            <TypeIcon className="w-3 h-3" />
                            <ChevronDown className="w-2 h-2 opacity-60" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent side="bottom" align="center" className={`${POPOVER_MENU_CN} min-w-[140px]`} data-testid="col-type-menu">
                          {Object.entries(CELL_TYPES).map(([type, cfg]) => (
                            <button
                              key={type}
                              onClick={() => setColumnType(col.id, type)}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 ${
                                colType === type ? cfg.color + " font-semibold" : "text-zinc-300"
                              }`}
                              data-testid={`col-type-option-${type}`}
                            >
                              <cfg.icon className="w-3.5 h-3.5" />
                              {cfg.label}
                              {colType === type && <span className="ml-auto text-[10px] opacity-60">active</span>}
                            </button>
                          ))}
                        </PopoverContent>
                      </Popover>
                      {!col.entityType && (
                        <Popover
                          open={anchorPicker === `col:${col.id}`}
                          onOpenChange={(o) => setAnchorPicker(o ? `col:${col.id}` : null)}
                        >
                          <PopoverTrigger asChild>
                            <button
                              className="flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium border border-zinc-700 bg-zinc-800/80 hover:border-zinc-500 text-zinc-400"
                              title="Attach a character or achievement to this column"
                              data-testid={`col-anchor-btn-${col.id}`}
                            >
                              <Paperclip className="w-3 h-3" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent side="bottom" align="center" className={`${POPOVER_MENU_CN} p-1.5`}>
                            <AnchorPickerMenu
                              characters={characters}
                              achievements={achievements}
                              notes={notes}
                              onSelect={(kind, entity) => { anchorCol(col.id, kind, entity); setAnchorPicker(null); }}
                              onClose={() => setAnchorPicker(null)}
                            />
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                    )}
                    {col.entityType ? (
                      <ColRowEntityDisplay
                        entity={resolveAnchorEntity(col.entityType, col.entityId)}
                        fallback={{ name: col.entityName, imageUrl: col.entityImageUrl }}
                        kind={col.entityType}
                        onClear={() => clearColAnchor(col.id)}
                        rotated={rotateHeaders}
                        imgMaxPx={imgMaxPx}
                      />
                    ) : (
                    <div className={`flex items-center gap-0.5 group ${rotateHeaders ? "flex-col" : ""}`}>
                      {editingHeader === `col:${col.id}` ? (
                        // Popover-based editor — portals to document.body
                        // via Radix so the input NEVER gets stacked
                        // behind other columns, popovers, or the grid
                        // toolbar. The rotated label anchor stays in
                        // the header slot as a visual reference.
                        <Popover open onOpenChange={(o) => { if (!o) setEditingHeader(null); }}>
                          <PopoverAnchor asChild>
                            <div
                              className="relative w-full"
                              style={{ height: `${rotatedHeaderHeightRem}rem` }}
                            />
                          </PopoverAnchor>
                          <PopoverContent
                            side="top"
                            align="start"
                            sideOffset={4}
                            className="bg-zinc-900 border border-cyan-500/60 rounded shadow-xl px-2 py-1.5 w-64"
                            data-testid={`col-title-popover-${col.id}`}
                          >
                            <div className="text-[10px] text-cyan-300/80 mb-1 font-medium">
                              Column {colIdx(col.id)} title
                            </div>
                            <Input
                              value={col.name}
                              onChange={e => updateCol(col.id, "name", e.target.value)}
                              onKeyDown={e => {
                                if (e.key === "Enter" || e.key === "Escape") setEditingHeader(null);
                              }}
                              autoFocus
                              placeholder={`Col ${colIdx(col.id)}`}
                              className="bg-zinc-800 border-zinc-600 text-white text-sm h-8 px-2 w-full"
                              data-testid={`col-title-input-${col.id}`}
                            />
                          </PopoverContent>
                        </Popover>
                      ) : rotateHeaders ? (
                        // Vertical (90°) header, reading BOTTOM-TO-TOP (tilt
                        // head left). Rotated via transform on an absolutely-
                        // centered label so its un-rotated width never widens
                        // the column. Columns can sit tightly side-by-side.
                        <div className="relative w-full flex justify-center" style={{ height: `${rotatedHeaderHeightRem}rem` }}>
                          {/* Column indicator — a thin colored tick centered
                              at the column's bottom that visually ties the
                              vertical label to its own column. Uses the group
                              color when the col belongs to a group. */}
                          <div
                            className="absolute pointer-events-none"
                            style={{
                              left: "50%",
                              transform: "translateX(-50%)",
                              bottom: 0,
                              height: "2px",
                              width: "60%",
                              background: col.groupId
                                ? (colGroupById[col.groupId]?.color || "#71717a")
                                : "#52525b",
                              opacity: 0.9,
                            }}
                          />
                          <div
                            role="button"
                            tabIndex={0}
                            onClick={() => setEditingHeader(`col:${col.id}`)}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setEditingHeader(`col:${col.id}`); }}
                            className={`absolute left-1/2 top-1/2 text-xs font-medium leading-tight cursor-pointer select-none ${col.name ? "text-zinc-100 hover:text-white" : "text-zinc-600 hover:text-zinc-400 italic"}`}
                            style={{
                              transform: "translate(-50%, -50%) rotate(-90deg)",
                              transformOrigin: "center",
                              whiteSpace: "nowrap",
                              maxWidth: `${rotatedHeaderHeightRem - 0.5}rem`,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              // Soft dark halo so overlapping/adjacent labels
                              // stay readable without a solid box.
                              textShadow: "0 0 6px rgba(10,10,10,0.95), 0 0 3px rgba(10,10,10,0.95), 0 1px 2px rgba(0,0,0,0.9)",
                              color: col.name && col.color ? col.color : undefined,
                            }}
                            title={col.name || `Col ${colIdx(col.id)}`}
                          >
                            {col.name || `Col ${colIdx(col.id)}`}
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setEditingHeader(`col:${col.id}`)}
                          className={`text-xs font-medium w-full text-center py-1 whitespace-normal break-words leading-tight ${col.name ? "text-zinc-300 hover:text-white" : "text-zinc-600 hover:text-zinc-400 italic"}`}
                          style={{ color: col.name && col.color ? col.color : undefined }}
                        >
                          {col.name || `Col ${colIdx(col.id)}`}
                        </button>
                      )}
                      {!marksMode && cols.length > 1 && (
                        <button
                          onClick={() => removeCol(col.id)}
                          className="text-zinc-700 hover:text-red-400 opacity-0 group-hover/col:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    )}
                  </th>
                );
              })}
              {hasRowTotals && (
                <th className="px-1 py-1 min-w-[3.5rem]">
                  <span className="text-[10px] text-zinc-500 font-medium">Total</span>
                </th>
              )}
              {countsActive && (
                <th className="px-1 py-1 min-w-[3rem]" data-testid="scorecard-count-col-header">
                  <span className="text-[11px] text-emerald-400 font-semibold" title="Checked count">✓</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              // Divider row
              if (row.isDivider) {
                // If interior to a row group, the group's side-bar rowSpan
                // already occupies the first column for this row, so exclude
                // that column from the colSpan and carry the group's right
                // border so the group box stays closed across the separator.
                const dRowEdge = rowGroupEdge[row.id];
                const coveredBySidebar = rowGroups.length > 0 && rowGroupPos[row.id] && !rowGroupPos[row.id].start;
                const dividerColSpan = cols.length + 1 + (hasRowTotals ? 1 : 0) + (countsActive ? 1 : 0) + (rowGroups.length > 0 && !coveredBySidebar ? 1 : 0);
                return (
                  <tr
                    key={row.id}
                    className={`group/row ${dragRowId === row.id ? "opacity-40" : ""}`}
                    onDragOver={onRowDragOver(row.id)}
                    onDrop={onRowDrop}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        if (dropRowTarget?.targetId === row.id) setDropRowTarget(null);
                      }
                    }}
                  >
                    <td
                      colSpan={dividerColSpan}
                      className={`py-0.5 ${rowDropClass(row.id)}`}
                      style={dRowEdge ? { borderRight: `2px solid ${dRowEdge.color}` } : undefined}
                    >
                      <div className="flex items-center gap-1.5">
                        {!marksMode && (
                        <button
                          type="button"
                          draggable
                          onDragStart={(e) => {
                            setDragRowId(row.id);
                            e.dataTransfer.effectAllowed = "move";
                            e.dataTransfer.setData("text/plain", `row:${row.id}`);
                          }}
                          onDragEnd={clearRowDrag}
                          className="cursor-grab active:cursor-grabbing text-zinc-600 hover:text-cyan-400 opacity-0 group-hover/row:opacity-100 transition-opacity flex-shrink-0"
                          title="Drag to reorder this divider row"
                        >
                          <GripVertical className="w-3 h-3" />
                        </button>
                        )}
                        {!marksMode && (
                        <Popover
                          open={colorPicker === row.id}
                          onOpenChange={(o) => setColorPicker(o ? row.id : null)}
                        >
                          <PopoverTrigger asChild>
                            <button
                              className="w-3 h-3 rounded-sm flex-shrink-0 border border-white/20"
                              style={{ backgroundColor: row.color }}
                            />
                          </PopoverTrigger>
                          <PopoverContent side="bottom" align="start" className={`${POPOVER_MENU_CN} p-1.5`}>
                            <div className="flex flex-wrap gap-1 w-[160px]">
                              {ROW_COLORS.map(c => (
                                <button
                                  key={c}
                                  onClick={() => { updateRow(row.id, "color", c); setColorPicker(null); }}
                                  className="w-4 h-4 rounded-sm border border-white/20 hover:scale-110 transition-transform"
                                  style={{ backgroundColor: c }}
                                />
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        )}
                        <div className="flex-1 h-[2px] rounded" style={{ backgroundColor: row.color }} />
                        {row.name !== undefined && (
                          <input
                            value={row.name}
                            onChange={e => updateRow(row.id, "name", e.target.value)}
                            placeholder="label"
                            readOnly={marksMode}
                            // Grows with typed content in modern browsers
                            // (Chrome 123+/Firefox 122+/Safari 17.4+; all
                            // are present in 2026). Fixed min/max clamps
                            // the growth so the divider stays useful even
                            // for long titles like "Exploration Accolades".
                            className="bg-transparent text-xs font-medium text-center outline-none border-none px-2 py-0.5 rounded min-w-[6rem] max-w-[24rem]"
                            style={{
                              color: row.color,
                              textShadow: "0 1px 2px rgba(0,0,0,0.6)",
                              fieldSizing: "content",
                            }}
                          />
                        )}
                        <div className="flex-1 h-[2px] rounded" style={{ backgroundColor: row.color }} />
                        {!marksMode && (
                        <button
                          onClick={() => removeRow(row.id)}
                          className="text-zinc-700 hover:text-red-400 opacity-0 group-hover/row:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              }

              // Data row
              const rgPos = rowGroupPos[row.id];
              const rg = rgPos?.groupId ? rowGroupById[rgPos.groupId] : null;
              const rEdge = rowGroupEdge[row.id];
              // Only render the side-bar <td> at the START of each run.
              // Grouped rows in the middle of a run inherit via rowSpan;
              // ungrouped rows still get a stub <td> so column alignment
              // stays in sync with the header row.
              const showSideBar = rowGroups.length > 0 && (!rgPos || rgPos.start);
              return (
                <tr
                  key={row.id}
                  className={`group/row ${dragRowId === row.id ? "opacity-40" : ""}`}
                  onDragOver={onRowDragOver(row.id)}
                  onDrop={onRowDrop}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      if (dropRowTarget?.targetId === row.id) setDropRowTarget(null);
                    }
                  }}
                >
                  {/* Row-group side bar — a colored vertical strip that
                      brackets each same-group run of rows. The group name is
                      ALWAYS shown as vertical (90°) text so it reads without
                      hovering. Forms the LEFT edge (+ rounded top/bottom-left
                      corners) of the bright row-group border. Ungrouped runs
                      still emit an empty stub <td> so column alignment with
                      the primary header stays intact. */}
                  {rowGroups.length > 0 && rgPos?.start && (
                    <td
                      rowSpan={rgPos.rowSpan}
                      className="sc-sticky-left w-[1.05rem] min-w-[1.05rem] p-0 align-middle relative"
                      style={{
                        left: 0,
                        zIndex: 11,
                        ...(rg ? {
                          borderLeft: `2px solid ${rg.color}`,
                          borderTop: `2px solid ${rg.color}`,
                          borderBottom: `2px solid ${rg.color}`,
                          borderTopLeftRadius: "8px",
                          borderBottomLeftRadius: "8px",
                          // Opaque base (from .sc-sticky-left) + colored strip
                          // overlay so grid cells never show through when
                          // scrolling horizontally.
                          backgroundImage: `linear-gradient(${rg.color}33, ${rg.color}33)`,
                        } : {}),
                      }}
                      title={rg?.name}
                      data-testid={rg ? `row-group-bar-${rg.id}` : undefined}
                    >
                      {rg ? (
                        // Absolutely centered in the FULL-HEIGHT cell (the td
                        // spans the whole run via rowSpan) so the vertical
                        // label is never clipped, no matter how tall/short the
                        // group is. Reads bottom-to-top.
                        <span
                          className="absolute left-1/2 top-1/2 text-[11px] font-semibold leading-none select-none pointer-events-none"
                          style={{
                            transform: "translate(-50%, -50%) rotate(-90deg)",
                            transformOrigin: "center",
                            whiteSpace: "nowrap",
                            color: "#fff",
                            textShadow: "0 1px 2px rgba(0,0,0,0.9)",
                          }}
                        >
                          {rg.name}
                        </span>
                      ) : null}
                    </td>
                  )}
                  <td
                    className={`sc-sticky-left pr-1 py-0.5 relative ${rowDropClass(row.id)}`}
                    style={{
                      width: `${rowLabelColWidthRem}rem`,
                      minWidth: `${rowLabelColWidthRem}rem`,
                      // Pin to the left just AFTER the row-group side bar (so
                      // the two never overlap / "sink" into each other), or at
                      // the very left when there are no row groups.
                      left: rowGroups.length > 0 ? "1.05rem" : "0px",
                      zIndex: 10,
                      // Opaque base (from .sc-sticky-left) + optional group
                      // tint overlay so grid cells never bleed through the
                      // row label when scrolling horizontally.
                      backgroundImage: row.groupId ? `linear-gradient(${rowGroupById[row.groupId]?.color}1f, ${rowGroupById[row.groupId]?.color}1f)` : undefined,
                      // Continue the row-group border's top/bottom edges
                      // across the label column.
                      borderTop: rEdge?.isFirst ? `2px solid ${rEdge.color}` : undefined,
                      borderBottom: rEdge?.isLast ? `2px solid ${rEdge.color}` : undefined,
                    }}
                  >
                    {row.entityType ? (
                      <div className="flex items-center gap-1">
                        {!marksMode && (
                        <Popover open={colorPicker === row.id} onOpenChange={(o) => setColorPicker(o ? row.id : null)}>
                          <PopoverTrigger asChild>
                            <button
                              draggable
                              onDragStart={(e) => { setDragRowId(row.id); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", `row:${row.id}`); }}
                              onDragEnd={clearRowDrag}
                              className="w-3 h-3 rounded-sm flex-shrink-0 border border-white/20 cursor-grab active:cursor-grabbing hover:ring-1 hover:ring-white/50 transition"
                              style={{ backgroundColor: row.color }}
                              title="Drag to reorder · click to set color"
                              data-testid={`row-handle-${row.id}`}
                            />
                          </PopoverTrigger>
                          <PopoverContent side="right" align="start" className={`${POPOVER_MENU_CN} p-1.5`}>
                            <div className="flex flex-wrap gap-1 w-[160px]">
                              {ROW_COLORS.map(c => (
                                <button
                                  key={c}
                                  onClick={() => { updateRow(row.id, "color", c); setColorPicker(null); }}
                                  className="w-4 h-4 rounded-sm border border-white/20 hover:scale-110 transition-transform"
                                  style={{ backgroundColor: c }}
                                />
                              ))}
                            </div>
                          </PopoverContent>
                        </Popover>
                        )}
                        <ColRowEntityDisplay
                          entity={resolveAnchorEntity(row.entityType, row.entityId)}
                          fallback={{ name: row.entityName, imageUrl: row.entityImageUrl }}
                          kind={row.entityType}
                          onClear={() => clearRowAnchor(row.id)}
                          inRow
                          imgMaxPx={imgMaxPx}
                        />
                        {!marksMode && rows.length > 1 && (
                          <button
                            onClick={() => removeRow(row.id)}
                            className="text-zinc-700 hover:text-red-400 opacity-0 group-hover/row:opacity-100 transition-opacity"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    ) : (
                    <div className="flex items-center gap-1">
                      {!marksMode && (
                      <Popover open={colorPicker === row.id} onOpenChange={(o) => setColorPicker(o ? row.id : null)}>
                        <PopoverTrigger asChild>
                          <button
                            draggable
                            onDragStart={(e) => { setDragRowId(row.id); e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", `row:${row.id}`); }}
                            onDragEnd={clearRowDrag}
                            className="w-3 h-3 rounded-sm flex-shrink-0 border border-white/20 cursor-grab active:cursor-grabbing hover:ring-1 hover:ring-white/50 transition"
                            style={{ backgroundColor: row.color }}
                            title="Drag to reorder · click to set color"
                            data-testid={`row-handle-${row.id}`}
                          />
                        </PopoverTrigger>
                        <PopoverContent side="right" align="start" className={`${POPOVER_MENU_CN} p-1.5`}>
                          <div className="flex flex-wrap gap-1 w-[160px]">
                            {ROW_COLORS.map(c => (
                              <button
                                key={c}
                                onClick={() => { updateRow(row.id, "color", c); setColorPicker(null); }}
                                className="w-4 h-4 rounded-sm border border-white/20 hover:scale-110 transition-transform"
                                style={{ backgroundColor: c }}
                              />
                            ))}
                          </div>
                        </PopoverContent>
                      </Popover>
                      )}
                      {/* Row type — matches the per-column type button on
                          the header row. Click to change every non-divider
                          cell in this row to a single type in one shot. */}
                      {(() => {
                        if (marksMode) return null;
                        const rowType = getRowType(row.id);
                        const RTIcon = CELL_TYPES[rowType].icon;
                        return (
                          <Popover
                            open={rowTypeMenu === row.id}
                            onOpenChange={(o) => setRowTypeMenu(o ? row.id : null)}
                          >
                            <PopoverTrigger asChild>
                              <button
                                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                                  CELL_TYPES[rowType].color
                                } bg-zinc-800/80 border-zinc-700 hover:border-zinc-500 opacity-0 group-hover/row:opacity-100`}
                                title={`Row type: ${CELL_TYPES[rowType].label}. Click to convert all cells in this row.`}
                                data-testid={`row-type-btn-${row.id}`}
                              >
                                <RTIcon className="w-3 h-3" />
                                <ChevronDown className="w-2 h-2 opacity-60" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent side="right" align="start" className={`${POPOVER_MENU_CN} min-w-[160px]`} data-testid="row-type-menu">
                              <div className="text-[10px] text-zinc-500 uppercase tracking-wider px-1 pb-1">
                                Set all cells in this row
                              </div>
                              {Object.entries(CELL_TYPES).map(([type, cfg]) => (
                                <button
                                  key={type}
                                  onClick={() => setRowType(row.id, type)}
                                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 ${
                                    rowType === type ? cfg.color + " font-semibold" : "text-zinc-300"
                                  }`}
                                  data-testid={`row-type-option-${type}`}
                                >
                                  <cfg.icon className="w-3.5 h-3.5" />
                                  {cfg.label}
                                  {rowType === type && <span className="ml-auto text-[10px] opacity-60">active</span>}
                                </button>
                              ))}
                            </PopoverContent>
                          </Popover>
                        );
                      })()}
                      {editingHeader === `row:${row.id}` ? (
                        <Input
                          value={row.name}
                          onChange={e => updateRow(row.id, "name", e.target.value)}
                          onBlur={() => setEditingHeader(null)}
                          onKeyDown={e => e.key === "Enter" && setEditingHeader(null)}
                          autoFocus
                          placeholder={`Row ${rowIdx(row.id)}`}
                          className="bg-zinc-800 border-zinc-600 text-xs h-6 px-1 flex-1"
                          style={{ color: row.color }}
                        />
                      ) : (
                        <button
                          onClick={() => setEditingHeader(`row:${row.id}`)}
                          className={`text-xs font-medium flex-1 text-left leading-tight ${row.name ? "" : "italic opacity-50"}`}
                          style={{ color: row.color, overflowWrap: "break-word" }}
                        >
                          {row.name || `Row ${rowIdx(row.id)}`}
                        </button>
                      )}
                      <Popover open={anchorPicker === `row:${row.id}`} onOpenChange={(o) => setAnchorPicker(o ? `row:${row.id}` : null)}>
                        <PopoverTrigger asChild>
                          <button
                            className={`text-zinc-600 hover:text-sky-400 opacity-0 group-hover/row:opacity-100 transition-opacity ${marksMode ? "hidden" : ""}`}
                            title="Attach a character or achievement to this row"
                            data-testid={`row-anchor-btn-${row.id}`}
                          >
                            <Paperclip className="w-3 h-3" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent side="right" align="start" className={`${POPOVER_MENU_CN} p-1.5`}>
                          <AnchorPickerMenu
                            characters={characters}
                            achievements={achievements}
                            notes={notes}
                            onSelect={(kind, entity) => { anchorRow(row.id, kind, entity); setAnchorPicker(null); }}
                            onClose={() => setAnchorPicker(null)}
                          />
                        </PopoverContent>
                      </Popover>
                      {!marksMode && rows.length > 1 && (
                        <button
                          onClick={() => removeRow(row.id)}
                          className="text-zinc-700 hover:text-red-400 opacity-0 group-hover/row:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    )}
                  </td>
                  {cols.map(col => {
                    // Divider column cell
                    if (col.isDivider) {
                      // If this divider is interior to a column group, carry
                      // the group's bottom border on the last row so the group
                      // box stays closed across the separator.
                      const dEdge = colGroupEdge[col.id];
                      const dLastRow = row.id === lastGridRowId;
                      return (
                        <td
                          key={col.id}
                          className={`relative p-0 w-2 min-w-[0.5rem] ${rowDropClass(row.id)} ${colDropClass(col.id)}`}
                          style={dEdge && dLastRow ? { borderBottom: `2px solid ${dEdge.color}` } : undefined}
                        >
                          {/* Continuous separator: full-height line with no
                              vertical padding so consecutive rows' segments
                              butt together into one solid line all the way
                              through the grid. */}
                          <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-[2px]" style={{ backgroundColor: col.color || "#3f3f46" }} />
                        </td>
                      );
                    }
                    const cell = getCell(row.id, col.id);
                    const key = cellKey(row.id, col.id);
                    const colHasText = columnHasText(col.id);
                    // Character cells expand vertically to accommodate the
                    // portrait (max 64x64). Text cells grow too so long
                    // entries don't get clipped.
                    const cellMinH = cell.type === "character" || cell.type === "image" || cell.type === "note"
                      ? (imgMaxPx === 16 ? "min-h-[20px]" : imgMaxPx === 32 ? "min-h-[36px]" : "min-h-[64px]")
                      : cell.type === "text" ? "min-h-[3rem]" : "h-8";
                    // Character cells cap width at 64px — matches the header
                    // constraint. If the column also has a text cell we drop
                    // the cap so the text has room to breathe.
                    const tdWidthClass = ((cell.type === "character" || cell.type === "image" || cell.type === "note") && !colHasText)
                      ? (imgMaxPx === 16 ? "w-[20px] min-w-[20px] max-w-[20px]" : imgMaxPx === 32 ? "w-[36px] min-w-[36px] max-w-[36px]" : "w-[64px] min-w-[64px] max-w-[64px]")
                      : "";
                    // Subtle background tint based on the col + row group
                    // this cell belongs to (matches the spec: "the whole
                    // column/row area under the group gets a subtle
                    // background tint"). Col tint layered above row tint
                    // via linear-gradient so both are visible when a cell
                    // sits in the intersection of two groups.
                    const colTint = col.groupId ? colGroupById[col.groupId]?.color : null;
                    const rowTint = row.groupId ? rowGroupById[row.groupId]?.color : null;
                    const cellBg = colTint && rowTint
                      ? `linear-gradient(${colTint}1f, ${colTint}1f), ${rowTint}1f`
                      : colTint ? `${colTint}1f`
                      : rowTint ? `${rowTint}1f`
                      : undefined;
                    const gEdge = colGroupEdge[col.id];
                    const rEdge = rowGroupEdge[row.id];
                    const isColLastRow = row.id === lastGridRowId;
                    // Row-group right edge lands on the last data column, unless
                    // a row-total column is shown (then it lands on that cell).
                    const rowRightHere = rEdge && !hasRowTotals && col.id === lastGridColId;
                    const cellStyle = {
                      ...(cellBg ? { background: cellBg } : {}),
                      // Vertical edges (column-group sides, or row-group right)
                      borderLeft: gEdge?.isFirst ? `2px solid ${gEdge.color}` : undefined,
                      borderRight: gEdge?.isLast ? `2px solid ${gEdge.color}`
                        : rowRightHere ? `2px solid ${rEdge.color}` : undefined,
                      // Horizontal edges (row-group top/bottom, or column-group bottom)
                      borderTop: rEdge?.isFirst ? `2px solid ${rEdge.color}` : undefined,
                      borderBottom: rEdge?.isLast ? `2px solid ${rEdge.color}`
                        : (gEdge && isColLastRow) ? `2px solid ${gEdge.color}` : undefined,
                      // Rounded corners — column-group bottoms + row-group right corners
                      borderBottomLeftRadius: (gEdge?.isFirst && isColLastRow) ? "8px" : undefined,
                      borderTopRightRadius: (rowRightHere && rEdge.isFirst) ? "8px" : undefined,
                      borderBottomRightRadius: (gEdge?.isLast && isColLastRow) ? "8px"
                        : (rowRightHere && rEdge.isLast) ? "8px" : undefined,
                    };
                    return (
                      <td
                        key={col.id}
                        className={`px-0.5 py-0.5 align-middle ${tdWidthClass} ${rowDropClass(row.id)} ${colDropClass(col.id)}`}
                        style={cellStyle}
                      >
                        <Popover
                          open={
                            symbolPicker === key ||
                            characterPicker === key ||
                            achievementPicker === key ||
                            notePicker === key ||
                            imagePicker === key
                          }
                          onOpenChange={(o) => {
                            if (!o) {
                              setSymbolPicker(null);
                              setCharacterPicker(null);
                              setAchievementPicker(null);
                              setNotePicker(null);
                              setImagePicker(null);
                            }
                          }}
                        >
                          <PopoverAnchor asChild>
                            <div className="relative">
                              {/* Mark overlay — check/X toggled by clicks in
                                  Marks mode. Positioned absolute so it can
                                  visually stamp any cell type (image, note,
                                  character, etc.) without touching that
                                  cell's own click handlers when marksMode
                                  is off (`pointer-events-none`). When
                                  marksMode is on, the overlay swallows the
                                  click to cycle the mark. Marks state
                                  (green check / red X) is always shown, so
                                  a checklist created from a template still
                                  displays ticks after the user toggles
                                  editing mode back on. */}
                              {(marksMode || cellMarks[key]) && (() => {
                                const mark = cellMarks[key] || "";
                                const overlayEl = (
                                  <div
                                    className={`absolute inset-0 z-20 flex items-center justify-center rounded ${
                                      marksMode ? "cursor-pointer" : "pointer-events-none"
                                    } ${mark ? "bg-black/40" : ""}`}
                                    onClick={marksMode ? (e) => {
                                      e.stopPropagation();
                                      cycleCellMark(row.id, col.id);
                                    } : undefined}
                                    data-testid={`cell-mark-${row.id}-${col.id}`}
                                    title={
                                      marksMode
                                        ? undefined
                                        : mark === "check" ? "Checked" : mark === "x" ? "Marked with X" : ""
                                    }
                                  >
                                    {mark === "check" && (
                                      <Check className="w-5 h-5 text-emerald-400 drop-shadow-[0_0_4px_rgba(0,0,0,0.9)]" strokeWidth={3} />
                                    )}
                                    {mark === "x" && (
                                      <X className="w-5 h-5 text-red-400 drop-shadow-[0_0_4px_rgba(0,0,0,0.9)]" strokeWidth={3} />
                                    )}
                                  </div>
                                );
                                // In Marks mode the overlay swallows pointer
                                // events, so surface the same hover popup on
                                // the overlay itself — the user can see WHAT
                                // they're checking off (character, achievement
                                // or note) even while marking.
                                if (marksMode) {
                                  if (cell.type === "character" && cell.value?.characterId) {
                                    return (
                                      <CharacterHover character={charById[cell.value.characterId]} fallback={cell.value}>
                                        {overlayEl}
                                      </CharacterHover>
                                    );
                                  }
                                  if (cell.type === "achievement" && cell.value?.achievementId) {
                                    return (
                                      <AchievementHover achievement={achById[cell.value.achievementId]} fallback={cell.value}>
                                        {overlayEl}
                                      </AchievementHover>
                                    );
                                  }
                                  if (cell.type === "note" && cell.value?.noteId) {
                                    return (
                                      <NoteHover value={cell.value}>
                                        {overlayEl}
                                      </NoteHover>
                                    );
                                  }
                                }
                                return overlayEl;
                              })()}
                              <div
                                className={`w-full ${cellMinH} rounded border border-zinc-700/60 bg-zinc-900/40 flex items-center justify-center cursor-pointer hover:border-zinc-500 transition-colors group/cell ${
                                  cellMarks[key] ? "opacity-50" : ""
                                }`}
                                onContextMenu={e => {
                                  if (locked) return;
                                  e.preventDefault();
                                  setCellTypeMenu(key);
                                }}
                              >
                                {cell.type === "checkbox" && (
                                  <button
                                    onClick={() => handleCellClick(row.id, col.id)}
                                    className={`w-5 h-5 rounded border-2 transition-colors flex items-center justify-center ${
                                      cell.value
                                        ? "bg-purple-500 border-purple-400"
                                        : "border-zinc-600 hover:border-zinc-400"
                                    }`}
                                    data-testid={`cell-${row.id}-${col.id}`}
                                  >
                                    {cell.value && <span className="text-white text-xs font-bold">{"\u2713"}</span>}
                                  </button>
                                )}
                                {cell.type === "symbol" && (
                                  <button
                                    onClick={() => handleCellClick(row.id, col.id)}
                                    className="text-sm font-medium text-zinc-300 hover:text-white w-full h-full flex items-center justify-center"
                                    data-testid={`cell-${row.id}-${col.id}`}
                                  >
                                    {cell.value || "\u00B7"}
                                  </button>
                                )}
                                {cell.type === "number" && (
                                  <input
                                    type="number"
                                    value={cell.value === 0 && !Number.isFinite(cell.value) ? "" : (cell.value ?? "")}
                                    onChange={e => {
                                      const v = e.target.value;
                                      setCell(row.id, col.id, { value: v === "" ? 0 : (parseFloat(v) || 0) });
                                    }}
                                    className="w-full h-full bg-transparent text-center text-sm text-zinc-200 focus:outline-none focus:ring-1 focus:ring-purple-500 rounded [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                    data-testid={`cell-${row.id}-${col.id}`}
                                  />
                                )}
                                {cell.type === "text" && (
                                  <Textarea
                                    value={cell.value || ""}
                                    onChange={e => {
                                      const v = e.target.value.slice(0, TEXT_CELL_MAX);
                                      setCell(row.id, col.id, { value: v });
                                    }}
                                    maxLength={TEXT_CELL_MAX}
                                    placeholder="…"
                                    rows={2}
                                    className="w-full min-h-[3rem] bg-transparent border-0 text-center text-xs text-zinc-200 placeholder:text-zinc-600 focus-visible:ring-1 focus-visible:ring-sky-500 resize-none overflow-hidden px-1 py-1 rounded whitespace-pre-wrap break-words"
                                    style={{ fieldSizing: "content" }}
                                    data-testid={`cell-${row.id}-${col.id}`}
                                  />
                                )}
                                {cell.type === "character" && (
                                  <button
                                    onClick={() => handleCellClick(row.id, col.id)}
                                    className="w-full h-full flex items-center justify-center p-0.5"
                                    data-testid={`cell-${row.id}-${col.id}`}
                                  >
                                    {cell.value?.characterId ? (
                                      <CharacterHover
                                        character={charById[cell.value.characterId]}
                                        fallback={cell.value}
                                      >
                                        {cell.value.imageUrl ? (
                                          <img
                                            src={cell.value.imageUrl}
                                            alt={cell.value.characterName || "character"}
                                            className="w-auto h-auto rounded object-contain"
                                            style={{ maxWidth: `${imgMaxPx}px`, maxHeight: `${imgMaxPx}px` }}
                                          />
                                        ) : (
                                          <span className="text-[10px] text-zinc-300 leading-tight text-center px-1 line-clamp-2">
                                            {cell.value.characterName || "Unnamed"}
                                          </span>
                                        )}
                                      </CharacterHover>
                                    ) : (
                                      <UserCircle2 className="w-5 h-5 text-zinc-600 hover:text-pink-400 transition-colors" />
                                    )}
                                  </button>
                                )}
                                {cell.type === "achievement" && (
                                  <button
                                    onClick={() => handleCellClick(row.id, col.id)}
                                    className="w-full h-full flex items-center justify-center p-0.5"
                                    data-testid={`cell-${row.id}-${col.id}`}
                                  >
                                    {cell.value?.achievementId ? (
                                      <AchievementHover
                                        achievement={achById[cell.value.achievementId]}
                                        fallback={cell.value}
                                      >
                                        {cell.value.imageUrl ? (
                                          <img
                                            src={cell.value.imageUrl}
                                            alt={cell.value.name || "achievement"}
                                            className="w-auto h-auto rounded object-contain"
                                            style={{ maxWidth: `${imgMaxPx}px`, maxHeight: `${imgMaxPx}px` }}
                                          />
                                        ) : (
                                          <span className="text-[10px] text-zinc-300 leading-tight text-center px-1 line-clamp-2">
                                            {cell.value.name || "Unnamed"}
                                          </span>
                                        )}
                                      </AchievementHover>
                                    ) : (
                                      <Trophy className="w-5 h-5 text-zinc-600 hover:text-yellow-400 transition-colors" />
                                    )}
                                  </button>
                                )}
                                {cell.type === "image" && (
                                  <button
                                    onClick={() => setImagePicker(key)}
                                    className="w-full h-full flex items-center justify-center p-0.5"
                                    data-testid={`cell-${row.id}-${col.id}`}
                                    title="Set image URL"
                                  >
                                    {cell.value ? (
                                      <img
                                        src={cell.value}
                                        alt=""
                                        className="w-auto h-auto rounded object-contain"
                                        style={{ maxWidth: `${imgMaxPx}px`, maxHeight: `${imgMaxPx}px` }}
                                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                                      />
                                    ) : (
                                      <ImageIcon className="w-5 h-5 text-zinc-600 hover:text-teal-400 transition-colors" />
                                    )}
                                  </button>
                                )}
                                {cell.type === "note" && (
                                  <button
                                    onClick={() => handleCellClick(row.id, col.id)}
                                    className="w-full h-full flex items-center justify-center p-0.5"
                                    data-testid={`cell-${row.id}-${col.id}`}
                                    title={cell.value?.title || cell.value?.text || "Pick a note"}
                                  >
                                    {cell.value?.noteId ? (
                                      cell.value.imageUrl ? (
                                        <img
                                          src={cell.value.imageUrl}
                                          alt={cell.value.title || "note"}
                                          className="w-auto h-auto rounded object-contain"
                                          style={{ maxWidth: `${imgMaxPx}px`, maxHeight: `${imgMaxPx}px` }}
                                        />
                                      ) : (
                                        <span className="text-[10px] text-amber-200 leading-tight text-center px-1 line-clamp-2">
                                          {cell.value.title || "Note"}
                                        </span>
                                      )
                                    ) : (
                                      <StickyNote className="w-5 h-5 text-zinc-600 hover:text-amber-300 transition-colors" />
                                    )}
                                  </button>
                                )}
                                <Popover
                                  open={cellTypeMenu === key}
                                  onOpenChange={(o) => setCellTypeMenu(o ? key : null)}
                                >
                                  <PopoverTrigger asChild>
                                    <button
                                      className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-zinc-700 border border-zinc-600 flex items-center justify-center opacity-0 group-hover/cell:opacity-100 transition-opacity"
                                      title="Change this cell's type"
                                    >
                                      <ChevronDown className="w-2 h-2 text-zinc-400" />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent side="bottom" align="start" className={`${POPOVER_MENU_CN} min-w-[130px]`}>
                                    {allowedCellTypes(row.id, col.id).map(type => {
                                      const cfg = CELL_TYPES[type];
                                      return (
                                        <button
                                          key={type}
                                          onClick={() => setCellType(row.id, col.id, type)}
                                          className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 ${
                                            cell.type === type ? cfg.color : "text-zinc-300"
                                          }`}
                                        >
                                          <cfg.icon className="w-3 h-3" />
                                          {cfg.label}
                                        </button>
                                      );
                                    })}
                                    {allowedCellTypes(row.id, col.id).length < Object.keys(CELL_TYPES).length && (
                                      <p className="text-[9px] text-zinc-500 px-2 pt-1 leading-tight">
                                        Some types are hidden because this row or column is anchored.
                                      </p>
                                    )}
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </div>
                          </PopoverAnchor>
                          <PopoverContent side="bottom" align="start" className={`${POPOVER_MENU_CN} p-1.5`}>
                            {characterPicker === key && (
                              <EntityPickerBody
                                kind="character"
                                items={characters}
                                currentId={cell.value?.characterId}
                                onSelect={(entity) => {
                                  const img = (entity.images && entity.images[0]) || null;
                                  setCell(row.id, col.id, { value: { characterId: entity.id, characterName: entity.title || "Unnamed", imageUrl: img } });
                                  setCharacterPicker(null);
                                }}
                                onClear={cell.value ? () => { setCell(row.id, col.id, { value: null }); setCharacterPicker(null); } : null}
                                testid={`character-picker-${row.id}-${col.id}`}
                              />
                            )}
                            {achievementPicker === key && (
                              <EntityPickerBody
                                kind="achievement"
                                items={achievements}
                                currentId={cell.value?.achievementId}
                                onSelect={(entity) => {
                                  setCell(row.id, col.id, { value: { achievementId: entity.id, name: entity.name || "Unnamed", imageUrl: entity.imageUrl || null } });
                                  setAchievementPicker(null);
                                }}
                                onClear={cell.value ? () => { setCell(row.id, col.id, { value: null }); setAchievementPicker(null); } : null}
                                testid={`achievement-picker-${row.id}-${col.id}`}
                              />
                            )}
                            {notePicker === key && (
                              <EntityPickerBody
                                kind="note"
                                items={notes}
                                currentId={cell.value?.noteId}
                                onSelect={(entity) => {
                                  setCell(row.id, col.id, { value: { noteId: entity.id, title: entity.title || (entity.text ? entity.text.slice(0, 40) : "Note"), imageUrl: entity.imageUrl || null } });
                                  setNotePicker(null);
                                }}
                                onClear={cell.value ? () => { setCell(row.id, col.id, { value: null }); setNotePicker(null); } : null}
                                testid={`note-picker-${row.id}-${col.id}`}
                              />
                            )}
                            {imagePicker === key && (
                              <ImageUrlPickerBody
                                initialUrl={cell.value || ""}
                                onSave={(url) => { setCell(row.id, col.id, { value: url }); setImagePicker(null); }}
                                onClear={cell.value ? () => { setCell(row.id, col.id, { value: "" }); setImagePicker(null); } : null}
                              />
                            )}
                            {symbolPicker === key && (
                              <div className="flex flex-wrap gap-1 w-[140px]">
                                {SYMBOL_OPTIONS.map(sym => (
                                  <button
                                    key={sym}
                                    onClick={() => { setCell(row.id, col.id, { value: cell.value === sym ? "" : sym }); setSymbolPicker(null); }}
                                    className={`w-6 h-6 rounded flex items-center justify-center text-sm hover:bg-zinc-700 ${
                                      cell.value === sym ? "bg-purple-500/30 text-purple-300" : "text-zinc-300"
                                    }`}
                                  >
                                    {sym}
                                  </button>
                                ))}
                                <button
                                  onClick={() => { setCell(row.id, col.id, { value: "" }); setSymbolPicker(null); }}
                                  className="w-6 h-6 rounded flex items-center justify-center text-xs text-zinc-500 hover:bg-zinc-700"
                                >
                                  clr
                                </button>
                              </div>
                            )}
                          </PopoverContent>
                        </Popover>
                      </td>
                    );
                  })}
                  {hasRowTotals && (
                    <td
                      className={`px-1 py-0.5 text-center ${rowDropClass(row.id)}`}
                      style={rEdge ? {
                        borderRight: `2px solid ${rEdge.color}`,
                        borderTop: rEdge.isFirst ? `2px solid ${rEdge.color}` : undefined,
                        borderBottom: rEdge.isLast ? `2px solid ${rEdge.color}` : undefined,
                        borderTopRightRadius: rEdge.isFirst ? "8px" : undefined,
                        borderBottomRightRadius: rEdge.isLast ? "8px" : undefined,
                      } : undefined}
                    >
                      {rowTotals[row.id] !== null && (
                        <span className="text-sm font-bold text-emerald-400" data-testid={`row-total-${row.id}`}>{rowTotals[row.id]}</span>
                      )}
                    </td>
                  )}
                  {countsActive && (
                    <td className="px-1 py-0.5 text-center" data-testid={`row-check-count-${row.id}`}>
                      {rowCheckCounts[row.id] > 0 && (
                        <span className="text-sm font-bold text-emerald-400">{rowCheckCounts[row.id]}</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}

            {/* Column totals row */}
            {cols.some(col => !col.isDivider && colTotals[col.id] !== null) && (
              <tr className="border-t border-zinc-700">
                <td className="pr-1 py-1">
                  <span className="text-xs text-zinc-500 font-medium">Total</span>
                </td>
                {cols.map(col => (
                  <td key={col.id} className={`px-0.5 py-1 text-center ${colDropClass(col.id)}`}>
                    {!col.isDivider && colTotals[col.id] !== null && (
                      <span className="text-sm font-bold text-purple-400" data-testid={`col-total-${col.id}`}>{colTotals[col.id]}</span>
                    )}
                  </td>
                ))}
                {hasRowTotals && (
                  <td className="px-1 py-1 text-center">
                    <span className="text-sm font-bold text-yellow-400" data-testid="grand-total">
                      {Object.values(colTotals).reduce((s, v) => s + (v || 0), 0)}
                    </span>
                  </td>
                )}
              </tr>
            )}

            {/* Column check-count row (Marks On mode) — mirrors the number
                totals row but tallies green checks per column at the bottom
                (opposite the column titles at the top). */}
            {countsActive && (
              <tr className="border-t border-zinc-700" data-testid="scorecard-count-row">
                {rowGroups.length > 0 && <td className="w-[1.05rem] min-w-[1.05rem]" />}
                <td className="pr-1 py-1 text-right">
                  <span className="text-[11px] text-emerald-400 font-semibold" title="Checked count">✓</span>
                </td>
                {cols.map(col => (
                  <td key={col.id} className="px-0.5 py-1 text-center">
                    {!col.isDivider && colCheckCounts[col.id] > 0 && (
                      <span className="text-sm font-bold text-emerald-400" data-testid={`col-check-count-${col.id}`}>{colCheckCounts[col.id]}</span>
                    )}
                  </td>
                ))}
                {hasRowTotals && <td />}
                <td />
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Note: Radix Popover portals to document.body and handles outside-click
          dismissal internally, so no manual overlay is needed. This lets menus
          overflow the grid's overflow-x-auto wrapper cleanly. */}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-1 flex-wrap">
        <Button size="sm" onClick={handleSave} className="bg-purple-600 hover:bg-purple-500 h-7 text-xs" data-testid="scorecard-save-btn">
          <Save className="w-3 h-3 mr-1" /> {dirty ? "Save" : "Saved"}
        </Button>
        {onCancel && (
          <Button size="sm" variant="ghost" onClick={onCancel} className="h-7 text-xs text-zinc-400">
            Cancel
          </Button>
        )}

        {/* Marks mode toggle — click cells to cycle unmarked → check → X
            → unmarked. Auto-enabled when the grid is from a template so
            template-based grids behave like checklists out of the box. */}
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setMarksMode((m) => !m)}
          className={`h-7 text-xs ${marksMode ? "text-emerald-300 bg-emerald-500/10" : "text-zinc-400 hover:text-white"}`}
          title={marksMode ? "Marks mode ON — click cells to check them off" : "Turn on Marks mode to click cells and cycle check/X"}
          data-testid="scorecard-marks-toggle"
        >
          <MousePointerClick className="w-3.5 h-3.5 mr-1" />
          {marksMode ? "Marks: On" : "Marks: Off"}
        </Button>

        {/* Lock/Unlock — when locked, structural edits (add/remove rows,
            column types, cell type picker) are disabled so a template
            grid can be used as a pure checklist without accidental edits. */}
        {fromTemplateId && (
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleLocked}
            className={`h-7 text-xs ${locked ? "text-amber-300 bg-amber-500/10" : "text-zinc-400 hover:text-white"}`}
            title={locked ? "Locked — click to unlock structural edits" : "Unlocked — click to relock this template grid"}
            data-testid="scorecard-lock-toggle"
          >
            {locked ? <Lock className="w-3.5 h-3.5 mr-1" /> : <Unlock className="w-3.5 h-3.5 mr-1" />}
            {locked ? "Locked" : "Unlocked"}
          </Button>
        )}

        {/* Templates picker — replaces this grid's structure with a
            template's rows/cols/cell defaults. Also offers "save as
            template" for the current structure (public or private). */}
        <Popover
          open={templatesOpen}
          onOpenChange={(o) => { setTemplatesOpen(o); if (o) loadTemplates(); }}
        >
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-zinc-400 hover:text-white"
              data-testid="scorecard-templates-btn"
              title="Apply a template to this grid"
            >
              <BookTemplate className="w-3.5 h-3.5 mr-1" />
              Templates
            </Button>
          </PopoverTrigger>
          <PopoverContent side="bottom" align="start" className="bg-zinc-800 border border-zinc-700 text-zinc-200 w-80 max-h-80 overflow-y-auto p-2">            {templatesLoading ? (
              <div className="flex items-center gap-2 text-xs text-zinc-400 py-3 justify-center">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading templates…
              </div>
            ) : templates.length === 0 ? (
              <p className="text-xs text-zinc-500 italic py-2 text-center">
                No templates yet for this game. Save the current grid as a template below.
              </p>
            ) : (
              <div className="space-y-1">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTemplate(t)}
                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 text-left"
                    data-testid={`scorecard-template-${t.id}`}
                  >
                    <BookTemplate className="w-3.5 h-3.5 text-purple-300 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-zinc-100 truncate">{t.name}</div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {t.isOwn ? "By you" : `By ${t.submittedByUsername || "another player"}`}
                        {" · "}
                        {(t.rows || []).length}r × {(t.cols || []).length}c
                        {t.useCount ? ` · used ${t.useCount}×` : ""}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            <div className="border-t border-zinc-700 mt-2 pt-2">
              {saveTemplateOpen ? (
                <div className="space-y-2">
                  <Input
                    value={newTemplateName}
                    onChange={(e) => setNewTemplateName(e.target.value)}
                    placeholder="Template name"
                    className="bg-zinc-900 border-zinc-700 text-white h-7 text-xs"
                    data-testid="scorecard-template-name-input"
                  />
                  <label className="flex items-center gap-2 text-xs text-zinc-300">
                    <input
                      type="checkbox"
                      checked={newTemplatePublic}
                      onChange={(e) => setNewTemplatePublic(e.target.checked)}
                      data-testid="scorecard-template-public-checkbox"
                    />
                    Share with community (pending review)
                  </label>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      onClick={saveAsTemplate}
                      disabled={savingTemplate || !newTemplateName.trim()}
                      className="h-7 text-xs bg-purple-600 hover:bg-purple-500"
                      data-testid="scorecard-template-save-btn"
                    >
                      {savingTemplate ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save template"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setSaveTemplateOpen(false); setNewTemplateName(""); }}
                      className="h-7 text-xs text-zinc-400"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setSaveTemplateOpen(true); setNewTemplateName(name || "Grid"); }}
                  className="w-full h-7 text-xs text-purple-300 hover:text-purple-200 hover:bg-purple-500/10 justify-start"
                  disabled={!scorecard?.id}
                  title={scorecard?.id ? "Save this grid's structure as a reusable template" : "Save the grid first, then re-open Templates to save as template"}
                  data-testid="scorecard-open-save-template"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" /> Save current as template
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>

        {/* Groups — column/row sections. Popover exposes both axes so the
            user can bundle N consecutive columns (or rows) into a named,
            colored group. Each cell in a grouped range gets a subtle
            background tint matching the group color. */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs text-zinc-400 hover:text-white"
              data-testid="scorecard-groups-btn"
              title="Manage column and row sections"
            >
              <Rows3 className="w-3.5 h-3.5 mr-1" />
              Groups
              {(colGroups.length + rowGroups.length) > 0 && (
                <span className="ml-1 text-[10px] text-cyan-300">
                  {colGroups.length}c/{rowGroups.length}r
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent side="bottom" align="start" className="bg-zinc-800 border border-zinc-700 text-zinc-200 w-96 max-h-[420px] overflow-y-auto p-2">
            <GroupsManagerBody
              axis="col"
              groups={colGroups}
              items={cols}
              onAddGroup={addColGroup}
              onUpdateGroup={updateColGroup}
              onDeleteGroup={deleteColGroup}
              onAssign={assignColToGroup}
            />
            <div className="h-px bg-zinc-700 my-3" />
            <GroupsManagerBody
              axis="row"
              groups={rowGroups}
              items={rows}
              onAddGroup={addRowGroup}
              onUpdateGroup={updateRowGroup}
              onDeleteGroup={deleteRowGroup}
              onAssign={assignRowToGroup}
            />
          </PopoverContent>
        </Popover>

        <div className="ml-auto flex items-center gap-1">
          {/* Default cell type — used for cells in newly-added rows /
              columns AND as the target for "Apply to all cells". The
              icon-only trigger sits next to the zoom button so it stays
              close to the other grid-wide toggles. */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-xs text-zinc-400 hover:text-white"
                title={`Default cell type: ${CELL_TYPES[defaultCellType]?.label || defaultCellType}`}
                data-testid="scorecard-default-type-btn"
              >
                {(() => {
                  const cfg = CELL_TYPES[defaultCellType] || CELL_TYPES.checkbox;
                  const Icon = cfg.icon;
                  return <><Icon className={`w-3.5 h-3.5 mr-1 ${cfg.color}`} /> {cfg.label}</>;
                })()}
              </Button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="end" className={`${POPOVER_MENU_CN} min-w-[180px]`}>
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider px-1 pb-1">
                Default type for new rows/cols
              </div>
              {Object.entries(CELL_TYPES).map(([type, cfg]) => (
                <button
                  key={type}
                  onClick={() => { setDefaultCellType(type); setDirty(true); }}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 ${
                    defaultCellType === type ? cfg.color + " font-semibold" : "text-zinc-300"
                  }`}
                  data-testid={`scorecard-default-type-option-${type}`}
                >
                  <cfg.icon className="w-3.5 h-3.5" />
                  {cfg.label}
                  {defaultCellType === type && <span className="ml-auto text-[10px] opacity-60">active</span>}
                </button>
              ))}
              <div className="border-t border-zinc-700 mt-1 pt-1">
                <button
                  onClick={() => applyDefaultTypeToEmpty()}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 text-sky-300"
                  data-testid="scorecard-apply-default-to-empty"
                >
                  <Square className="w-3.5 h-3.5" />
                  Apply to empty cells only
                </button>
                <button
                  onClick={() => applyDefaultTypeToAll()}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 text-purple-300"
                  data-testid="scorecard-apply-default-to-all"
                >
                  <Check className="w-3.5 h-3.5" />
                  Apply to all cells
                </button>
              </div>
            </PopoverContent>
          </Popover>

          {marksMode && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setShowCounts(v => !v); setDirty(true); }}
              className={`h-7 flex-shrink-0 text-xs ${showCounts ? "text-emerald-400" : "text-zinc-400 hover:text-white"}`}
              title="Show a count of checked cells at the end of each row and column"
              data-testid="scorecard-counts-toggle"
            >
              <Sigma className="w-3.5 h-3.5 mr-1 flex-shrink-0" />
              Counts
            </Button>
          )}

          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setZoom(zoom === 1 ? 0.5 : zoom === 0.5 ? 0.25 : 1); setDirty(true); }}
            className="h-7 w-[104px] flex-shrink-0 justify-start text-xs text-zinc-400 hover:text-white"
            title={`Grid image size: ${imgMaxPx}px — click to cycle 64 → 32 → 16px`}
            data-testid="scorecard-zoom-toggle"
          >
            {zoom === 0.25 ? <ZoomIn className="w-3.5 h-3.5 mr-1 flex-shrink-0" /> : <ZoomOut className="w-3.5 h-3.5 mr-1 flex-shrink-0" />}
            <span className="tabular-nums">Zoom {imgMaxPx}</span>
          </Button>
        </div>
      </div>

      {/* Bulk cell-type conversion confirm — 3-way modal so users can
          overwrite the filled cells, keep them and convert only empties,
          or bail out entirely. Rendered inside the editor so it inherits
          the dialog styling and doesn't leak outside grid context. */}
      {typeChangeConfirm && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60"
          data-testid="type-change-confirm-backdrop"
          onClick={() => setTypeChangeConfirm(null)}
        >
          <div
            className="bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl w-[420px] max-w-[90vw] p-4"
            onClick={(e) => e.stopPropagation()}
            data-testid="type-change-confirm-dialog"
          >
            <div className="text-sm font-semibold text-white mb-1">
              {typeChangeConfirm.title}
            </div>
            <div className="text-xs text-zinc-400 mb-3 leading-relaxed">
              {typeChangeConfirm.message}
              <br />
              <span className="text-white/70">Pick an option:</span>
            </div>
            <div className="flex flex-col gap-2">
              <Button
                size="sm"
                onClick={() => { typeChangeConfirm.apply("overwrite"); setTypeChangeConfirm(null); }}
                className="bg-red-600 hover:bg-red-500 text-white justify-start text-xs h-8"
                data-testid="type-change-overwrite-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-2" />
                Clear filled cells and convert everything
              </Button>
              <Button
                size="sm"
                onClick={() => { typeChangeConfirm.apply("keep"); setTypeChangeConfirm(null); }}
                className="bg-emerald-600 hover:bg-emerald-500 text-white justify-start text-xs h-8"
                data-testid="type-change-keep-btn"
              >
                <Check className="w-3.5 h-3.5 mr-2" />
                Keep filled cells, convert only empties ({typeChangeConfirm.filledCount} kept)
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setTypeChangeConfirm(null)}
                className="text-zinc-400 hover:text-white justify-start text-xs h-8"
                data-testid="type-change-cancel-btn"
              >
                <X className="w-3.5 h-3.5 mr-2" />
                Cancel &mdash; don&apos;t change anything
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ============ Helper Components ============

// Hover card wrapper for a character portrait — shows name, first ~4
// statistics, and note text. Falls back to the value stored in the cell
// (characterName + imageUrl) if the live character can't be found.
function CharacterHover({ character, fallback, children }) {
  const c = character || {};
  const displayName = c.title || fallback?.characterName || "Unnamed";
  const img = (c.images && c.images[0]) || fallback?.imageUrl || null;
  const stats = (c.statistics || []).slice(0, 4);
  return (
    <HoverCard openDelay={200} closeDelay={80}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent className="w-80 max-w-[90vw] bg-zinc-900 border-zinc-700 text-zinc-200 p-3" side="top">
        <div className="flex items-start gap-2">
          {img && (
            <img src={img} alt="" className="max-w-[40%] h-auto max-h-40 object-contain rounded bg-black/40 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-pink-300 break-words text-left">{displayName}</div>
          </div>
        </div>
        {c.text && <div className="text-[11px] text-zinc-400 mt-2 whitespace-pre-wrap break-words text-left">{c.text}</div>}
        {stats.length > 0 && (
          <div className="mt-2 grid grid-cols-2 gap-1">
            {stats.map(s => (
              <div key={s.id || s.name} className="flex justify-between gap-1 text-[10px] bg-black/30 rounded px-1.5 py-0.5">
                <span className="text-pink-300/80 truncate">{s.name}</span>
                <span className="text-zinc-200 tabular-nums truncate">{String(s.value ?? "")}</span>
              </div>
            ))}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

// Hover card wrapper for an achievement — shows name, unlock status, score,
// and description. Falls back to the value stored on the cell if the live
// achievement can't be found.
function AchievementHover({ achievement, fallback, children }) {
  const a = achievement || {};
  const displayName = a.name || fallback?.name || "Unnamed";
  const img = a.imageUrl || fallback?.imageUrl || null;
  return (
    <HoverCard openDelay={200} closeDelay={80}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent className="w-80 max-w-[90vw] bg-zinc-900 border-zinc-700 text-zinc-200 p-3" side="top">
        <div className="flex items-start gap-2">
          {img && (
            <img src={img} alt="" className="max-w-[40%] h-auto max-h-40 object-contain rounded bg-black/40 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-yellow-300 break-words text-left">{displayName}</div>
            <div className="text-[10px] flex items-center gap-1.5 mt-0.5">
              {a.isUnlocked ? (
                <span className="text-emerald-400">Unlocked</span>
              ) : (
                <span className="text-zinc-500">Locked</span>
              )}
              {typeof a.score === "number" && a.score > 0 && (
                <span className="text-zinc-400">· {a.score}</span>
              )}
            </div>
          </div>
        </div>
        {a.description && (
          <div className="text-[11px] text-zinc-400 mt-2 whitespace-pre-wrap break-words text-left">{a.description}</div>
        )}
      </HoverCardContent>
    </HoverCard>
  );
}

// Hover card wrapper for a note — shows title, text, and image. Used both
// on note cells and note anchors so users can see what a checkbox refers to.
function NoteHover({ note, value, children }) {
  const v = value || {};
  const n = note || {};
  const title = v.title || n.title || "Note";
  const text = v.text || n.text || "";
  const img = v.imageUrl || n.imageUrl || null;
  return (
    <HoverCard openDelay={200} closeDelay={80}>
      <HoverCardTrigger asChild>
        {children}
      </HoverCardTrigger>
      <HoverCardContent className="w-80 max-w-[90vw] bg-zinc-900 border-zinc-700 text-zinc-200 p-3" side="top">
        <div className="flex items-start gap-2">
          {img && (
            <img src={img} alt="" className="max-w-[40%] h-auto max-h-40 object-contain rounded bg-black/40 flex-shrink-0" />
          )}
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-amber-300 break-words text-left">{title}</div>
          </div>
        </div>
        {text && <div className="text-[11px] text-zinc-400 mt-2 whitespace-pre-wrap break-words text-left">{text}</div>}
      </HoverCardContent>
    </HoverCard>
  );
}

// Row/col anchor picker content — shows kind chooser (Character/Achievement)
// first, then lists entities of the chosen kind for the user to select.
// This is placed inside a PopoverContent by the caller, so it does not
// Groups manager (col + row). Renders one axis at a time; the parent
// stacks two instances (cols on top, rows on bottom) inside the Groups
// popover. Kept as a small local helper because its UI is identical
// between axes — only the label copy and the underlying items differ.
function GroupsManagerBody({ axis, groups, items, onAddGroup, onUpdateGroup, onDeleteGroup, onAssign }) {
  const axisLabel = axis === "col" ? "Column" : "Row";
  const memberIndex = { }; // groupId -> array of items in that group
  const nonDivider = items.filter(i => !i.isDivider);
  nonDivider.forEach(it => {
    const gid = it.groupId || "__none__";
    (memberIndex[gid] = memberIndex[gid] || []).push(it);
  });
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#3b82f6");
  const [pickerMembers, setPickerMembers] = useState({}); // groupId -> boolean menu open
  const swatches = ["#3b82f6", "#a855f7", "#ec4899", "#ef4444", "#f97316", "#eab308", "#22c55e", "#14b8a6", "#06b6d4", "#64748b"];

  const displayName = (it) => (axis === "col"
    ? (it.name || `Col ${nonDivider.indexOf(it) + 1}`)
    : (it.name || `Row ${nonDivider.indexOf(it) + 1}`)
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-300 font-medium">{axisLabel} groups</div>
        {!creating && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => { setCreating(true); setNewName(""); setNewColor("#3b82f6"); }}
            className="h-6 text-[10px] text-cyan-300 hover:text-cyan-200 hover:bg-cyan-500/10 px-2"
            data-testid={`groups-add-${axis}-btn`}
          >
            <Plus className="w-3 h-3 mr-1" />Add
          </Button>
        )}
      </div>
      {creating && (
        <div className="rounded border border-zinc-700 bg-zinc-900/40 p-2 space-y-1.5">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={`${axisLabel} group name`}
            className="bg-zinc-900 border-zinc-700 h-7 text-xs"
            data-testid={`groups-${axis}-name-input`}
            autoFocus
          />
          <div className="flex flex-wrap gap-1">
            {swatches.map(c => (
              <button
                key={c}
                onClick={() => setNewColor(c)}
                className={`w-5 h-5 rounded-sm border ${newColor === c ? "border-white ring-2 ring-white/40" : "border-white/20"}`}
                style={{ backgroundColor: c }}
                title={c}
              />
            ))}
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="sm"
              onClick={() => {
                if (!newName.trim()) return;
                onAddGroup(newName.trim(), newColor, []);
                setCreating(false);
              }}
              disabled={!newName.trim()}
              className="h-6 text-[10px] bg-purple-600 hover:bg-purple-500"
              data-testid={`groups-${axis}-create-btn`}
            >
              Create
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setCreating(false)}
              className="h-6 text-[10px] text-zinc-400"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      {groups.length === 0 && !creating && (
        <p className="text-[11px] text-zinc-500 italic">No {axisLabel.toLowerCase()} groups yet.</p>
      )}
      {groups.map(g => {
        const members = memberIndex[g.id] || [];
        return (
          <div key={g.id} className="rounded border border-zinc-700 bg-zinc-900/40 p-2 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    className="w-4 h-4 rounded-sm border border-white/20 shrink-0"
                    style={{ backgroundColor: g.color }}
                    title="Change color"
                  />
                </PopoverTrigger>
                <PopoverContent side="right" className={`${POPOVER_MENU_CN} p-1.5`}>
                  <div className="flex flex-wrap gap-1 w-[160px]">
                    {swatches.map(c => (
                      <button
                        key={c}
                        onClick={() => onUpdateGroup(g.id, { color: c })}
                        className="w-5 h-5 rounded-sm border border-white/20 hover:scale-110 transition-transform"
                        style={{ backgroundColor: c }}
                      />
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <Input
                value={g.name}
                onChange={(e) => onUpdateGroup(g.id, { name: e.target.value })}
                className="bg-zinc-900 border-zinc-700 h-6 text-xs flex-1"
              />
              <Popover
                open={!!pickerMembers[g.id]}
                onOpenChange={(o) => setPickerMembers(prev => ({ ...prev, [g.id]: o }))}
              >
                <PopoverTrigger asChild>
                  <Button size="sm" variant="ghost" className="h-6 text-[10px] text-zinc-300 hover:text-white px-2">
                    {members.length} member{members.length === 1 ? "" : "s"}
                    <ChevronDown className="w-2.5 h-2.5 ml-1 opacity-60" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent side="right" align="start" className={`${POPOVER_MENU_CN} p-1.5 max-h-[240px] overflow-y-auto w-[180px]`}>
                  <div className="text-[10px] text-zinc-500 uppercase tracking-wider px-1 pb-1">
                    {axisLabel}s in group
                  </div>
                  {nonDivider.map(it => (
                    <label key={it.id} className="flex items-center gap-2 px-1 py-1 rounded text-xs hover:bg-zinc-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={it.groupId === g.id}
                        onChange={(e) => onAssign(it.id, e.target.checked ? g.id : null)}
                      />
                      <span className="truncate">{displayName(it)}</span>
                    </label>
                  ))}
                </PopoverContent>
              </Popover>
              <button
                onClick={() => {
                  if (window.confirm(`Delete group "${g.name}"? Members will become ungrouped.`)) {
                    onDeleteGroup(g.id);
                  }
                }}
                className="text-zinc-500 hover:text-red-400 shrink-0"
                title="Delete group"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            {members.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {members.map(m => (
                  <span
                    key={m.id}
                    className="text-[10px] px-1.5 py-0.5 rounded border border-zinc-700 bg-zinc-800 text-zinc-300"
                    title={displayName(m)}
                  >
                    {displayName(m).slice(0, 16)}
                  </span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// position itself — it just renders the menu body.
function AnchorPickerMenu({ characters, achievements, notes = [], onSelect, onClose }) {
  const [kindChoice, setKindChoice] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [entityQuery, setEntityQuery] = useState("");
  const items = kindChoice === "character"
    ? characters
    : kindChoice === "achievement"
      ? achievements
      : kindChoice === "note"
        ? notes
        : [];
  // Alphabetical sort + text search once a kind is chosen — matches the
  // in-cell EntityPickerBody behavior so users can find a specific
  // character/achievement/note fast even on games with big lists.
  const nameOf = (it, k) => (
    k === "character"
      ? (it.title || "")
      : k === "achievement"
        ? (it.name || "")
        : (it.title || it.text || "")
  );
  const sortedItems = useMemo(() => {
    if (!kindChoice) return [];
    const q = entityQuery.trim().toLowerCase();
    // Rank matches so TITLE matches always rank above description-only
    // matches (a title starting with the query ranks highest). Ties break
    // alphabetically. 0=title prefix, 1=title contains, 2=source, 3=description.
    const rankOf = (it) => {
      const n = nameOf(it, kindChoice).toLowerCase();
      if (n.startsWith(q)) return 0;
      if (n.includes(q)) return 1;
      if (kindChoice === "achievement") {
        if ((it._source || "").toLowerCase().includes(q)) return 2;
        if ((it.description || "").toLowerCase().includes(q)) return 3;
      }
      return 99;
    };
    const filtered = q ? items.filter(it => rankOf(it) < 99) : items;
    return [...filtered].sort((a, b) => {
      if (q) {
        const dr = rankOf(a) - rankOf(b);
        if (dr !== 0) return dr;
      }
      return nameOf(a, kindChoice).localeCompare(
        nameOf(b, kindChoice),
        undefined,
        { sensitivity: "base", numeric: true }
      );
    });
  }, [items, entityQuery, kindChoice]);
  return (
    <div
      className="w-[260px] max-h-[320px] overflow-y-auto"
      onClick={(e) => e.stopPropagation()}
    >
      {!kindChoice ? (
        <>
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider px-1 pb-1">Attach to</div>
          <button
            onClick={() => setKindChoice("character")}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 text-pink-300"
          >
            <UserCircle2 className="w-3.5 h-3.5" /> Character…
          </button>
          <button
            onClick={() => setKindChoice("achievement")}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 text-yellow-300"
          >
            <Trophy className="w-3.5 h-3.5" /> Achievement…
          </button>
          <button
            onClick={() => setKindChoice("note")}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 text-amber-200"
          >
            <StickyNote className="w-3.5 h-3.5" /> Note…
          </button>
          <button
            onClick={() => setKindChoice("image")}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-700 text-teal-300"
          >
            <ImageIcon className="w-3.5 h-3.5" /> Image URL…
          </button>
          <button
            onClick={onClose}
            className="w-full mt-1 px-2 py-1 rounded text-[11px] text-zinc-500 hover:bg-zinc-700 text-left"
          >
            Cancel
          </button>
        </>
      ) : kindChoice === "image" ? (
        <>
          <div className="flex items-center justify-between px-1 pb-1">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Image URL</span>
            <button onClick={() => setKindChoice(null)} className="text-[10px] text-zinc-500 hover:text-zinc-300">
              ← Back
            </button>
          </div>
          <div className="p-1 space-y-1.5">
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.png"
              className="w-full bg-zinc-900 border border-zinc-700 rounded text-xs px-2 py-1.5 text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-teal-500"
              autoFocus
            />
            <button
              onClick={() => {
                const v = imageUrl.trim();
                if (!v) return;
                onSelect("image", { imageUrl: v });
              }}
              disabled={!imageUrl.trim()}
              className="w-full px-2 py-1.5 rounded text-xs bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white transition-colors"
            >
              Attach image
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between px-1 pb-1">
            <span className="text-[10px] text-zinc-500 uppercase tracking-wider">
              {kindChoice === "character" ? "Character" : kindChoice === "achievement" ? "Achievement" : "Note"}
            </span>
            <button onClick={() => setKindChoice(null)} className="text-[10px] text-zinc-500 hover:text-zinc-300">
              ← Back
            </button>
          </div>
          {items.length > 3 && (
            <Input
              value={entityQuery}
              onChange={(e) => setEntityQuery(e.target.value)}
              placeholder={`Search ${kindChoice}s…`}
              className="bg-zinc-900 border-zinc-700 text-white h-7 text-xs mb-1"
              autoFocus
            />
          )}
          {sortedItems.length === 0 ? (
            <p className="text-xs text-zinc-500 px-2 py-2 leading-snug">
              {entityQuery
                ? `No ${kindChoice}s match "${entityQuery}".`
                : kindChoice === "character"
                  ? "No character notes yet. Add one first."
                  : kindChoice === "achievement"
                    ? "No achievements found for this game."
                    : "No notes yet. Create a plain note first."}
            </p>
          ) : (
            sortedItems.map(it => {
              const label = kindChoice === "character"
                ? (it.title || "Unnamed")
                : kindChoice === "achievement"
                  ? (it.name || "Unnamed")
                  : (it.title || (it.text ? it.text.slice(0, 40) : "Note"));
              const subtitle = kindChoice === "achievement" ? (it._source || null) : null;
              const img = kindChoice === "character"
                ? ((it.images && it.images[0]) || null)
                : kindChoice === "achievement"
                  ? (it.imageUrl || null)
                  : (it.imageUrl || null);
              return (
                <button
                  key={it.id}
                  onClick={() => onSelect(kindChoice, it)}
                  className="w-full flex items-center gap-2 px-1.5 py-1 rounded text-left hover:bg-zinc-700 transition-colors"
                >
                  {img ? (
                    <img src={img} alt="" className="w-7 h-7 rounded object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-7 h-7 rounded bg-zinc-700 flex items-center justify-center flex-shrink-0">
                      {kindChoice === "character"
                        ? <UserCircle2 className="w-4 h-4 text-zinc-500" />
                        : kindChoice === "achievement"
                          ? <Trophy className="w-4 h-4 text-zinc-500" />
                          : <StickyNote className="w-4 h-4 text-zinc-500" />}
                    </div>
                  )}
                  <span className="flex-1 min-w-0">
                    <span className="text-xs text-zinc-200 block truncate">{label}</span>
                    {subtitle && (
                      <span className="text-[10px] text-zinc-500 block truncate">{subtitle}</span>
                    )}
                  </span>
                </button>
              );
            })
          )}
        </>
      )}
    </div>
  );
}

// Anchored row/col header display — shows a small portrait, name, and a
// clear-anchor X. Hover-card reveals full entity info.
function ColRowEntityDisplay({ entity, fallback, kind, onClear, rotated, inRow, imgMaxPx = 64 }) {
  const displayName = kind === "character"
    ? (entity?.title || fallback?.name || "Unnamed")
    : kind === "achievement"
      ? (entity?.name || fallback?.name || "Unnamed")
      : kind === "note"
        ? (entity?.title || (entity?.text ? entity.text.slice(0, 40) : "") || fallback?.name || "Note")
        : ""; // image anchor — no name
  const img = kind === "character"
    ? ((entity?.images && entity.images[0]) || fallback?.imageUrl || null)
    : kind === "achievement"
      ? (entity?.imageUrl || fallback?.imageUrl || null)
      : kind === "note"
        ? (entity?.imageUrl || fallback?.imageUrl || null)
        : (fallback?.imageUrl || null); // image anchor stores URL on fallback
  const HoverWrap = kind === "character" ? CharacterHover : kind === "achievement" ? AchievementHover : kind === "note" ? NoteHover : null;
  const hoverProps = kind === "character"
    ? { character: entity, fallback: { characterName: displayName, imageUrl: img } }
    : kind === "achievement"
      ? { achievement: entity, fallback: { name: displayName, imageUrl: img } }
      : kind === "note"
        ? { note: entity, value: { title: displayName, text: entity?.text, imageUrl: img } }
        : null;
  const accent = kind === "character"
    ? "text-pink-300"
    : kind === "achievement"
      ? "text-yellow-300"
      : kind === "note"
        ? "text-amber-200"
        : "text-teal-300";

  // In-row image node — respects imgMaxPx (16/32/64) and never stretches.
  const smallImgSize = inRow ? 24 : (imgMaxPx >= 64 ? 40 : imgMaxPx === 32 ? 32 : 18);
  const smallImg = img ? (
    <img
      src={img}
      alt=""
      className="rounded object-contain flex-shrink-0"
      style={{ maxWidth: `${smallImgSize}px`, maxHeight: `${smallImgSize}px`, width: "auto", height: "auto" }}
    />
  ) : null;
  const fallbackIcon = kind === "character"
    ? <UserCircle2 className={`${inRow ? "w-5 h-5" : "w-8 h-8"} text-pink-400/70 flex-shrink-0`} />
    : kind === "achievement"
      ? <Trophy className={`${inRow ? "w-5 h-5" : "w-8 h-8"} text-yellow-400/70 flex-shrink-0`} />
      : kind === "note"
        ? <StickyNote className={`${inRow ? "w-5 h-5" : "w-8 h-8"} text-amber-300/70 flex-shrink-0`} />
        : <ImageIcon className={`${inRow ? "w-5 h-5" : "w-8 h-8"} text-teal-300/70 flex-shrink-0`} />;

  const badge = smallImg || fallbackIcon;
  const wrapper = HoverWrap ? (
    <HoverWrap {...hoverProps}>
      <span className={inRow ? "inline-flex items-center gap-1.5 min-w-0" : "inline-flex flex-col items-center gap-0.5"}>
        {badge}
        {!!displayName && (
          <span className={`${inRow ? "text-xs" : "text-[10px]"} font-medium ${inRow ? "truncate" : "truncate max-w-[70px]"} ${accent}`} title={displayName}>
            {displayName}
          </span>
        )}
      </span>
    </HoverWrap>
  ) : (
    <span className={inRow ? "inline-flex items-center gap-1.5 min-w-0" : "inline-flex flex-col items-center gap-0.5"} title={displayName || undefined}>
      {badge}
      {!!displayName && (
        <span className={`${inRow ? "text-xs" : "text-[10px]"} font-medium ${inRow ? "truncate" : "truncate max-w-[70px]"} ${accent}`}>
          {displayName}
        </span>
      )}
    </span>
  );

  if (inRow) {
    return (
      <div className="flex items-center gap-1.5 flex-1 min-w-0">
        {wrapper}
        <button
          onClick={onClear}
          className="text-zinc-600 hover:text-red-400 opacity-0 group-hover/row:opacity-100 transition-opacity"
          title="Remove anchor"
        >
          <X className="w-3 h-3" />
        </button>
      </div>
    );
  }

  // Column-anchor display
  return (
    <div className={`flex items-center gap-1 group ${rotated ? "flex-col" : "justify-center"}`}>
      {wrapper}
      <button
        onClick={onClear}
        className="text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Remove anchor"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}

// Cell picker body — used for both character and achievement cell values.
// Positioning-agnostic; place inside a PopoverContent.
function EntityPickerBody({ kind, items, currentId, onSelect, onClear, testid }) {
  const kindLabel = kind === "character" ? "Character" : kind === "achievement" ? "Achievement" : "Note";
  const emptyMsg = kind === "character"
    ? "No character notes on this game yet. Add a character note first."
    : kind === "achievement"
      ? "No achievements found for this game."
      : "No notes yet. Create a plain note first — attach an image URL if you want a thumbnail here.";
  const accentActive = kind === "character"
    ? "bg-pink-500/20 text-pink-300"
    : kind === "achievement"
      ? "bg-yellow-500/20 text-yellow-300"
      : "bg-amber-400/20 text-amber-200";

  // Search + alphabetical sort. Applied to every picker kind so long lists
  // stay navigable. Achievement picker also shows a `_source` subtitle
  // (base game / section / DLC name / Challenges) so duplicate names are
  // distinguishable at a glance.
  const [query, setQuery] = useState("");
  const filteredSorted = useMemo(() => {
    const nameOf = (it) => (
      kind === "character"
        ? (it.title || "")
        : kind === "achievement"
          ? (it.name || "")
          : (it.title || it.text || "")
    );
    const q = query.trim().toLowerCase();
    // Rank so TITLE matches rank above description-only matches (title
    // prefix highest). 0=title prefix, 1=title contains, 2=source, 3=desc.
    const rankOf = (it) => {
      const n = nameOf(it).toLowerCase();
      if (n.startsWith(q)) return 0;
      if (n.includes(q)) return 1;
      if (kind === "achievement") {
        if ((it._source || "").toLowerCase().includes(q)) return 2;
        if ((it.description || "").toLowerCase().includes(q)) return 3;
      }
      return 99;
    };
    const filtered = q ? items.filter(it => rankOf(it) < 99) : items;
    return [...filtered].sort((a, b) => {
      if (q) {
        const dr = rankOf(a) - rankOf(b);
        if (dr !== 0) return dr;
      }
      return nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: "base", numeric: true });
    });
  }, [items, query, kind]);

  return (
    <div
      className="w-[260px] max-h-[320px] flex flex-col"
      data-testid={testid}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-1 pb-1">
        <div className="text-[10px] text-zinc-500 uppercase tracking-wider">{kindLabel}</div>
        {items.length > 0 && (
          <div className="text-[10px] text-zinc-500">
            {query ? `${filteredSorted.length}/${items.length}` : items.length}
          </div>
        )}
      </div>
      {items.length > 3 && (
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={`Search ${kindLabel.toLowerCase()}s…`}
          className="bg-zinc-900 border-zinc-700 text-white h-7 text-xs mb-1"
          data-testid={`${testid || "picker"}-search`}
          autoFocus
        />
      )}
      <div className="flex-1 overflow-y-auto min-h-0">
      {filteredSorted.length === 0 ? (
        <p className="text-xs text-zinc-500 px-2 py-2 leading-snug">
          {query ? `No ${kindLabel.toLowerCase()}s match "${query}".` : emptyMsg}
        </p>
      ) : (
        filteredSorted.map(it => {
          const label = kind === "character"
            ? (it.title || "Unnamed")
            : kind === "achievement"
              ? (it.name || "Unnamed")
              : (it.title || (it.text ? it.text.slice(0, 40) : "Note"));
          const subtitle = kind === "achievement" ? (it._source || null) : null;
          const img = kind === "character"
            ? ((it.images && it.images[0]) || null)
            : (it.imageUrl || null);
          const active = it.id === currentId;
          return (
            <button
              key={it.id}
              onClick={() => onSelect(it)}
              className={`w-full flex items-center gap-2 px-1.5 py-1 rounded text-left hover:bg-zinc-700 transition-colors ${active ? accentActive : ""}`}
            >
              {img ? (
                <img src={img} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="w-8 h-8 rounded bg-zinc-700 flex items-center justify-center flex-shrink-0">
                  {kind === "character"
                    ? <UserCircle2 className="w-5 h-5 text-zinc-500" />
                    : kind === "achievement"
                      ? <Trophy className="w-5 h-5 text-zinc-500" />
                      : <StickyNote className="w-5 h-5 text-zinc-500" />}
                </div>
              )}
              <span className="flex-1 min-w-0">
                <span className={`text-xs block truncate ${active ? "font-medium" : "text-zinc-200"}`}>
                  {label}
                </span>
                {subtitle && (
                  <span className="text-[10px] text-zinc-500 block truncate">{subtitle}</span>
                )}
              </span>
            </button>
          );
        })
      )}
      </div>
      {onClear && (
        <button
          onClick={onClear}
          className="w-full mt-1 px-2 py-1 rounded text-[11px] text-zinc-500 hover:bg-zinc-700 hover:text-red-400 text-left"
        >
          Clear selection
        </button>
      )}
    </div>
  );
}

// Simple image-URL picker used by "image" cell type — user pastes a hotlink
// and we stash the URL string as the cell value.
function ImageUrlPickerBody({ initialUrl = "", onSave, onClear }) {
  const [url, setUrl] = useState(initialUrl);
  return (
    <div className="w-[260px] space-y-1.5" onClick={(e) => e.stopPropagation()}>
      <div className="text-[10px] text-zinc-500 uppercase tracking-wider px-1">Image URL</div>
      <input
        type="text"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://example.com/image.png"
        className="w-full bg-zinc-900 border border-zinc-700 rounded text-xs px-2 py-1.5 text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-teal-500"
        autoFocus
      />
      {url.trim() && (
        <div className="p-1 rounded border border-zinc-700 bg-zinc-900/60 flex items-center justify-center">
          <img
            src={url.trim()}
            alt="preview"
            className="max-w-[64px] max-h-[64px] w-auto h-auto object-contain"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
      )}
      <div className="flex items-center gap-1">
        <button
          onClick={() => onSave(url.trim())}
          disabled={!url.trim() && !initialUrl}
          className="flex-1 px-2 py-1.5 rounded text-xs bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white transition-colors"
        >
          Save
        </button>
        {onClear && (
          <button
            onClick={onClear}
            className="px-2 py-1.5 rounded text-xs text-zinc-400 hover:bg-zinc-700 hover:text-red-400"
          >
            Clear
          </button>
        )}
      </div>
    </div>
  );
}
